import React, { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import DebugService from '../services/DebugService';
import {
  Button,
  Checkbox,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import APIConstants from '../services/APIConstants';
import {
  InstanceActionList,
  dbInventoryColumnList,
  vmInventoryActivityEventColumnList,
  vmInventoryColumnList,
  vmInventoryIdleEventColumnList,
  vmScheduledEventColumnList,
  vmssInventoryActivityEventColumnList,
  vmssInventoryColumnList,
  vmssInventoryIdleEventColumnList,
  vmssScheduledEventColumnList
} from '../services/InventoryService';
import SnackbarNotification, { SNACKBAR_THEME } from './common/SnackbarNotification';
import InventoryApiService from '../services/InventoryApiService';
import { useLocation, useNavigate } from 'react-router-dom';
import DataGridTable from './common/DataGridTable';
import DataGridTableWithCheckbox from './common/DataGridTableWithCheckbox';
import MultiSelect from './common/MultiSelect.jsx';
import NewTable from './common/Table.jsx';
import VMSSInventoryTable from './common/inventory/VMSSInventoryTable';
import TableLoader from './common/contentloader/TableLoader';
import './common/Styles.css'
import {
  AZURE_DB_TYPE,
  CONST_SITE_LABEL,
  // BLOCK_CONTAINER_ICON,
  INVENTORY_EVENTS,
  setScopedItem,
  STORAGE_KEYS,
  SUBSCRIPTION_STATUS,
  TRIAL_SUBSCRIPTION_DATA_LIMIT,
  useCurrentInstanceType,
  USER_ROLE,
} from './common/Helper';
import InstanceScheduler from './common/inventory/InstanceScheduler';
import { HiOutlineRefresh } from "react-icons/hi";
import { RiDeleteBinLine } from "react-icons/ri";
import { FaRegEdit } from "react-icons/fa";
import { format } from 'date-fns';
import ConfirmModal from './common/ConfirmModal';
import dayjs from 'dayjs';
import { LoadingButton, TabContext, TabList, TabPanel } from '@mui/lab';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import TimezoneSelect from 'react-timezone-select';
import SubscriptionApiService from '../services/SubscriptionApiService';
import { useAuth } from '../state/AuthContext';
import ScheduleIdle from './common/inventory/ScheduleIdle';
import { useScopedLocalStorage } from '../hooks/useScopedLocalStorage';
import ModifyASG from './common/inventory/ModifyASG';
import { CONST_ROUTE_PATH } from '../route/RouteConstants';

// Extend dayjs with UTC and Timezone plugins
dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Helper to determine which actions should be disabled based on selected instances
 * @param {Array} selectedInstances - Array of selected instance objects
 * @param {string} type - Resource type (vm, vmss, mysql, postgresql, sql)
 * @returns {Set} Set of disabled action names
 */
export function getDisabledActions(selectedInstances = [], type = '') {
  const disabled = new Set();
  if (!selectedInstances.length) return disabled;

  const isStopped = (instance) => {
    if (type === 'vm') {
      return (instance?.vmStatus ?? '').toLowerCase() === 'deallocated';
    }
    if (type === 'vmss') {
      return (instance?.currentCapacity ?? 1) === 0;
    }
    if (['mysql', 'postgresql', 'sql'].includes(type)) {
      return ['stopped', 'stopping'].includes((instance?.state ?? '').toLowerCase());
    }
    return false;
  };

  const hasAnyStopped = selectedInstances.some(isStopped);
  const hasAnyRunning = selectedInstances.some(i => !isStopped(i));

  if (!hasAnyStopped) disabled.add('START_NOW');

  if (!hasAnyRunning) {
    disabled.add('STOP_NOW');
    disabled.add('SCHEDULE_LATER');
    disabled.add('SCHEDULE_IDLE');
  }

  if (type === 'vmss') {
    if (selectedInstances.every(isStopped)) disabled.add('SCALE_DOWN');
    if (selectedInstances.every(i => !isStopped(i))) disabled.add('SCALE_DEFAULT');
  }

  return disabled;
}

// State pill for the new-design DB inventory table
const dbStatePill = (state) => {
  const s = (state ?? '').toLowerCase();
  const cls = ['stopped', 'stopping', 'disabled', 'deallocated'].includes(s)
    ? 'st-stopped'
    : ['ready', 'running', 'available', 'succeeded'].includes(s)
      ? 'st-running'
      : 'st-unknown';
  return <span className={`status-pill ${cls}`}>{state ?? '—'}</span>;
};

// DB inventory columns for the new common/Table (mirrors dbInventoryColumnList fields)
const dbNewColumns = [
  { key: 'id', label: 'ID', render: (r) => <span title={String(r.id)}>{String(r.id).slice(0, 8)}…</span> },
  { key: 'serverName', label: 'Name' },
  { key: 'availabilityZone', label: 'Zone', className: 'num' },
  { key: 'location', label: 'Location' },
  { key: 'state', label: 'State', render: (r) => dbStatePill(r.state) },
  { key: 'skuName', label: 'SKU' },
  { key: 'skuTier', label: 'Tier' },
  { key: 'resourceGroup', label: 'Resource Group' },
  { key: 'peakCpuValue', label: 'Peak CPU', className: 'num', render: (r) => (r.peakCpuValue != null && r.peakCpuValue !== '' ? r.peakCpuValue : '—') },
  { key: 'engineType', label: 'Type' },
  {
    key: 'totalSavingCost', label: 'Saving Cost', className: 'num',
    render: (r) => {
      const tc = r.totalSavingCost;
      const val = tc && typeof tc === 'object' ? (tc.totalCost ?? 0) : (Number(tc) || 0);
      const dur = tc && typeof tc === 'object' ? tc.totalDuration : undefined;
      return <span title={dur != null ? `Total Duration: ${dur}` : undefined}>${Number(val).toFixed(2)}</span>;
    },
  },
];

// Shared Saving-Cost cell for the new-design inventory tables
const savingCostCell = (tc) => {
  const val = tc && typeof tc === 'object' ? (tc.totalCost ?? 0) : (Number(tc) || 0);
  const dur = tc && typeof tc === 'object' ? tc.totalDuration : undefined;
  const rate = tc && typeof tc === 'object' ? tc.onDemandPriceValue : undefined;
  const title = [
    dur != null ? `Total Duration: ${dur}` : null,
    rate != null ? `On Demand $/hr: $${Number(rate).toFixed(2)}` : null,
  ].filter(Boolean).join(' · ');
  return <span title={title || undefined}>${Number(val).toFixed(2)}</span>;
};

// Tags chips cell (array of { Key, Value })
const TagsCell = ({ tags }) => {
  const list = Array.isArray(tags) ? tags : [];
  if (!list.length) return <span style={{ color: 'var(--muted)', fontStyle: 'italic' }}>No tags</span>;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
      {list.map((t, i) => (
        <span key={i} title={`${t.Key}: ${t.Value}`} style={{ background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 4, padding: '1px 6px', fontSize: 11, whiteSpace: 'nowrap' }}>
          <b style={{ color: '#111827' }}>{t.Key}</b><span style={{ color: '#6b7280' }}>: {t.Value}</span>
        </span>
      ))}
    </div>
  );
};

// VM instance columns for the new common/Table (mirrors vmInventoryColumnList)
const vmNewColumns = [
  { key: 'id', label: 'ID', render: (r) => <span title={String(r.id)}>{String(r.id).slice(0, 8)}…</span> },
  { key: 'name', label: 'Name' },
  { key: 'vmStatus', label: 'Status', render: (r) => dbStatePill(r.vmStatus) },
  { key: 'location', label: 'Location' },
  { key: 'tags', label: 'Tags', render: (r) => <TagsCell tags={r.tags} /> },
  { key: 'provisioningState', label: 'Provisioning State' },
  { key: 'diskSizeGB', label: 'Disk Size (GB)', className: 'num' },
  { key: 'resourceGroup', label: 'Resource Group' },
  { key: 'vmSize', label: 'Size' },
  { key: 'osType', label: 'OS' },
  { key: 'peakCpuValue', label: 'Peak CPU', className: 'num', render: (r) => (r.peakCpuValue != null && r.peakCpuValue !== '' ? r.peakCpuValue : '—') },
  { key: 'timeCreated', label: 'Created At' },
  { key: 'totalSavingCost', label: 'Saving Cost', className: 'num', render: (r) => savingCostCell(r.totalSavingCost) },
];

// Idle Events columns for the new common/Table (mirrors vmInventoryIdleEventColumnList)
const idleNewColumns = [
  { key: 'InstanceId', label: 'Instance ID' },
  { key: 'IdleDays', label: 'Idle Days', className: 'num' },
  { key: 'Region', label: 'Region' },
  { key: 'AddedBy', label: 'Scheduled By' },
];

// Scheduled Events columns (VM/VMSS/DB share the same fields; id + group id hidden)
const scheduledEventNewColumns = [
  { key: 'instanceId', label: 'Instance ID' },
  { key: 'instanceName', label: 'Name' },
  { key: 'region', label: 'Region' },
  { key: 'serviceType', label: 'Type' },
  { key: 'stopTime', label: 'Stop At' },
  { key: 'startTime', label: 'Start At' },
  { key: 'processedStop', label: 'Processed Stop' },
  { key: 'processedStart', label: 'Processed Start' },
  { key: 'addedBy', label: 'Scheduled By' },
  { key: 'type', label: 'Scheduled Type' },
];

// Activity Events columns (handles both VM and VMSS cost shapes)
const activityEventNewColumns = [
  { key: 'id', label: 'S.no', className: 'num' },
  { key: 'instanceId', label: 'Instance Id' },
  { key: 'region', label: 'Region' },
  { key: 'endTime', label: 'Stopped At' },
  { key: 'stoppedBy', label: 'Stopped By' },
  { key: 'startTime', label: 'Started At' },
  { key: 'startedBy', label: 'Started By' },
  { key: 'instanceType', label: 'Instance Type' },
  {
    key: 'costingDetails', label: 'Cost', className: 'num',
    render: (r) => {
      const c = r.costingDetails ?? {};
      const inst = c.instanceCostingDetails?.[0] ?? {};
      const cost = Number(c.totalCost ?? c.cost ?? 0);
      const dur = inst.duration ?? c.totalDuration ?? c.duration ?? 0;
      const rate = Number(inst.onDemandPriceValue ?? c.onDemandPriceValue ?? 0);
      return <span title={`Total Duration: ${dur} · On Demand $/hr: $${rate.toFixed(2)}`}>${cost.toFixed(2)}</span>;
    },
  },
];

export default function AzureInventory() {
  const [selectedTab, setSelectedTab] = useState(INVENTORY_EVENTS.INSTANCES)
  const location = useLocation()
  // const inventoryCase = useRef('')
  const { type } = useCurrentInstanceType()

  useEffect(() => {
    document.title = `${CONST_SITE_LABEL} | ${type.toLocaleUpperCase()} Inventory`
  }, [location.pathname])

  return (
    <Grid container spacing={1} className='inventory-page'>
      <Grid item xs={12}>
        <TabContext value={selectedTab}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <TabList onChange={(e, currentTab) => setSelectedTab(currentTab)} aria-label="Inventory Tab List">
              <Tab label={INVENTORY_EVENTS.INSTANCES} value={INVENTORY_EVENTS.INSTANCES} className='tab' />
              <Tab label={INVENTORY_EVENTS.SCHEDULED} value={INVENTORY_EVENTS.SCHEDULED} className='tab' />
              {['vm'].includes(type) && <Tab label={INVENTORY_EVENTS.IDLE} value={INVENTORY_EVENTS.IDLE} className='tab' />}
              <Tab label={INVENTORY_EVENTS.ACTIVITY} value={INVENTORY_EVENTS.ACTIVITY} className='tab' />
              {/* <Tab label={INVENTORY_EVENTS.MODIFICATION} value={INVENTORY_EVENTS.MODIFICATION} className='tab' /> */}
            </TabList>
          </Box>
          <TabPanel value={INVENTORY_EVENTS.INSTANCES} className='tab-panel'><CurrentInstanceComponent /></TabPanel>
          <TabPanel value={INVENTORY_EVENTS.SCHEDULED} className='tab-panel'><ScheduledEvents /></TabPanel>
          <TabPanel value={INVENTORY_EVENTS.IDLE} className='tab-panel'><IdleEventsComponent /></TabPanel>
          <TabPanel value={INVENTORY_EVENTS.ACTIVITY} className='tab-panel'><ActivityLogComponent /></TabPanel>
          {/* <TabPanel value={INVENTORY_EVENTS.MODIFICATION} className='tab-panel'><ModificationLogComponent /></TabPanel> */}
        </TabContext>
      </Grid>
    </Grid>
  )
}

/**
 * Current Instance Component
 * @param {*} param0 
 * @returns 
 */
export function CurrentInstanceComponent() {

  const [Opendialog, setOpendialog] = useState(false);
  const [openModifyASGDialog, setOpenModifyASGDialog] = useState(false);
  const [openScheduleIdleDialog, setOpenScheduleIdleDialog] = useState(false);
  const [instanceAction, setInstanceAction] = useState(null);
  const [actionStatus, setActionStatus] = useState(null);
  const [actionStatusMessage, setActionStatusMessage] = useState(null);
  const [rows, setRows] = useState([])
  const [selectedInstances, setSelectedInstances] = useState([]);
  // const [selectedGlobalInstances, setSelectedGlobalInstances] = useState([]);
  const [showLoading, setShowLoading] = useState(false);
  const [refresh, setRefresh] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [showActivateNowConfirmModal, setShowActivateNowConfirmModal] = useState(false)
  // const [selectedAction, setSelectedAction] = useState('')
  // const [totalInstancesCount, setTotalInstancesCount] = useState(0)
  // const [totalRunningInstancesCount, setTotalRunningInstancesCount] = useState(0)
  // const [totalStoppedInstancesCount, setTotalStoppedInstancesCount] = useState(0)
  const [currentUserAccess, setCurrentUserAccess] = useState(null)
  // const [defaultHideColumnsForCustom, setDefaultHideColumnsForCustom] = React.useState({});
  const [actionBy, setActionBy] = React.useState('DROPDOWN');
  const [dataColumns, setDataColumns] = React.useState([]);
  const [filterTagList, setFilterTagList] = useState([]);
  // const [paginationData, setPaginationData] = useState({
  //   pageSize: 25,
  //   currentPage: 1
  // });

  // New filter state
  const [filterData, setFilterData] = useState({
    selectedFilter: '',
    tagKey: '',
    tagValues: [],
    filterValue: ''
  });

  const [currentInstanceApiData, setCurrentInstanceApiData] = useState({})

  // Filter options for Azure resources
  const commonFilterOptions = [
    { value: 'tags', label: 'Tags' },
    { value: 'location', label: 'Location' }
  ];
  const vmssFilterOptions = [
    { value: 'vmssName', label: 'Name' },
    ...commonFilterOptions
  ];
  const vmFilterOptions = [
    { value: 'vmName', label: 'Name' },
    { value: 'vmId', label: 'Id' },
    ...commonFilterOptions
  ];
  const dbFilterOptions = [
    { value: 'name', label: 'Name' },
    ...commonFilterOptions
  ];

  const location = useLocation()
  const { type } = useCurrentInstanceType()
  const { userDetails, refetchUserDetails, selectedAccount } = useAuth()
  const { getScopedItem } = useScopedLocalStorage()
  const navigate = useNavigate()

  /**
   * Fetch tag keys and values based on current resource type
   */
  const fetchTagData = async () => {
    // setShowLoading(true);
    DebugService.log(`Fetching Inventory Tag List ...`)

    try {
      setFilterTagList([])

      const dataRequest = {
        type: type.toLocaleUpperCase(),
        userAccountId: selectedAccount
      }

      switch (dataRequest.type) {
        case 'MYSQL':
          dataRequest.type = 'MYSQL_DATABASE'
          break
        case 'POSTGRESQL':
          dataRequest.type = 'POSTGRESQL_DATABASE'
          break
        case 'SQL':
          dataRequest.type = 'SQL_MANAGED_INSTANCE_DATABASE'
          break
        default:
          dataRequest.type = type.toLocaleUpperCase()
      }

      InventoryApiService.getAzureFilterTagList(APIConstants.ENDPOINT_AZURE_FILTER_TAG, dataRequest)
        .then(res => {
          DebugService.log('Inventory: Data', res)
          setFilterTagList(res['data'] ?? [])
          // setShowLoading(false);
        })
        .catch(err => {
          // setShowLoading(false);
          DebugService.error('Inventory: Error caught while fetching filter tag list', err)
        })

    } catch (error) {
      // setShowLoading(false);
      DebugService.error('Inventory: Error caught while fetching filter tag list', error)
    } finally {
      // setRefresh(false)
    }
  };

  /**
   * Action column to instant Start and Stop RDS instance
   */
  // const rdsActionGridColumn = {
  //   field: 'action',
  //   headerName: 'Action',
  //   // width: '200',
  //   align: 'center',
  //   renderCell: (params) => (
  //     ['stopped', 'available'].includes(params['row']['Status'].toLowerCase())
  //     && <Link className="action-link"
  //       title={params['row']['Status'].toLowerCase() === 'available' ? 'Click to Stop' : 'Click to Start'}
  //       onClick={(e) => {
  //         e.preventDefault()
  //         handleGridColumnAction(
  //           (params['row']['Status'].toLowerCase() === 'available' ? 'STOP_NOW' : 'START_NOW'),
  //           params['row']['id']
  //         )
  //       }}
  //       href="#">
  //       {params['row']['Status'].toLowerCase() === 'available' ? 'Stop Now' : 'Start Now'}
  //     </Link>
  //   )
  // }

  useEffect(() => {
    if (type === 'vm') {
      setDataColumns(vmInventoryColumnList)
    } else if (type === 'vmss') {
      setDataColumns(vmssInventoryColumnList)
    } else if (['mysql', 'postgresql', 'sql'].includes(type)) {
      setDataColumns(dbInventoryColumnList)
    }
  }, [type])

  /**
   * @description Fetch data
   */
  const fetchData = async () => {
    setShowLoading(true);
    DebugService.log(`Fetching Inventory Data...`)

    try {
      setRows([])

      let endpoint = ''

      switch (type) {
        case 'vmss': endpoint = APIConstants.ENDPOINT_AZURE_INVENTORY_VMSS
          break;
        case 'mysql':
        case 'postgresql':
        case 'sql':
          endpoint = APIConstants.ENDPOINT_AZURE_INVENTORY_DB
          break;
        default: endpoint = APIConstants.ENDPOINT_AZURE_INVENTORY_VM
      }

      const dataRequest = {}
      if (['mysql', 'postgresql', 'sql'].includes(type)) {
        dataRequest['serviceType'] = AZURE_DB_TYPE[type] ?? ''
      }

      InventoryApiService.getAzureInventory(selectedAccount, endpoint, dataRequest, filterData)
        .then(res => {
          DebugService.log('Azure Inventory: Data', res)

          setCurrentInstanceApiData(res?.data ? res : {})

          if (type === 'vm' && res['data']) {
            // let testData = (res['data'] || []).filter(d => d['InstanceId'] === 'i-07742696ff6fb9137')
            let testData = res['data'] || []

            if ((userDetails['subscriptionStatus'] && [SUBSCRIPTION_STATUS.TRIAL_ACTIVE].includes(userDetails['subscriptionStatus'])) || (getScopedItem(STORAGE_KEYS.SUBSCRIPTION_STATUS) && [SUBSCRIPTION_STATUS.TRIAL_ACTIVE].includes(getScopedItem(STORAGE_KEYS.SUBSCRIPTION_STATUS)))) {
              testData = testData.slice(0, TRIAL_SUBSCRIPTION_DATA_LIMIT)
            }

            // setTotalInstancesCount(res['data']['totalInstanceCount'] ?? 0)
            // setTotalRunningInstancesCount(res['data']['runningInstanceCount'] ?? 0)
            // setTotalStoppedInstancesCount(res['data']['stoppedInstanceCount'] ?? 0)

            if (testData.length > 0) {
              setRows(prev => testData.map(instance => ({
                ...instance,
                'timeCreated': format(instance.timeCreated, 'dd-MM-yyyy HH:mm:ss'),
                // 'action': handleGridColumnAction
              })))
            }
          }

          if (type === 'vmss' && res['data']) {
            // let testData = (res['data'] || []).filter(d => d['InstanceId'] === 'i-07742696ff6fb9137')
            let testData = res['data'] || []

            if ((userDetails['subscriptionStatus'] && [SUBSCRIPTION_STATUS.TRIAL_ACTIVE].includes(userDetails['subscriptionStatus'])) || (getScopedItem(STORAGE_KEYS.SUBSCRIPTION_STATUS) && [SUBSCRIPTION_STATUS.TRIAL_ACTIVE].includes(getScopedItem(STORAGE_KEYS.SUBSCRIPTION_STATUS)))) {
              testData = testData.slice(0, TRIAL_SUBSCRIPTION_DATA_LIMIT)
            }

            // setTotalInstancesCount(res['data']['totalInstanceCount'] ?? 0)
            // setTotalRunningInstancesCount(res['data']['runningInstanceCount'] ?? 0)
            // setTotalStoppedInstancesCount(res['data']['stoppedInstanceCount'] ?? 0)

            if (testData.length > 0) {
              setRows(prev => testData.map(instance => ({
                ...instance,
                'timeCreated': format(instance.timeCreated, 'dd-MM-yyyy HH:mm:ss'),
                // 'action': handleGridColumnAction
              })))
            }
          }

          if (['mysql', 'postgresql', 'sql'].includes(type) && res['data']) {
            // let testData = (res['data'] || []).filter(d => d['InstanceId'] === 'i-07742696ff6fb9137')
            let testData = res['data'] || []

            if ((userDetails['subscriptionStatus'] && [SUBSCRIPTION_STATUS.TRIAL_ACTIVE].includes(userDetails['subscriptionStatus'])) || (getScopedItem(STORAGE_KEYS.SUBSCRIPTION_STATUS) && [SUBSCRIPTION_STATUS.TRIAL_ACTIVE].includes(getScopedItem(STORAGE_KEYS.SUBSCRIPTION_STATUS)))) {
              testData = testData.slice(0, TRIAL_SUBSCRIPTION_DATA_LIMIT)
            }

            // setTotalInstancesCount(res['data']['totalInstanceCount'] ?? 0)
            // setTotalRunningInstancesCount(res['data']['runningInstanceCount'] ?? 0)
            // setTotalStoppedInstancesCount(res['data']['stoppedInstanceCount'] ?? 0)

            if (testData.length > 0) {
              // SQL response has some missing fields as compared to others
              if (type !== 'sql') {
                setRows(prev => testData.map(instance => ({
                  ...instance,
                  'createdAt': format(instance.createdAt, 'dd-MM-yyyy HH:mm:ss'),
                  // 'action': handleGridColumnAction
                })))
              } else {
                setRows(testData)
              }
            }
          }

          setShowLoading(false);
        })
        .catch(err => {
          setShowLoading(false);
          DebugService.error('Azure Inventory: Error caught while fetching data', err)
        })

    } catch (error) {
      setShowLoading(false);
      DebugService.error('Current Inventory Type: Fetch data error', error)
    } finally {
      setRefresh(false)
    }
  }

  useEffect(() => {
      DebugService.log(`Azure Inventory: Path changed, type: ${type}`)

      const userDetails = getScopedItem(STORAGE_KEYS.LOCAL_USER_DETAILS)
      const parseToJson = JSON.parse(userDetails)
      setCurrentUserAccess(parseToJson['roleName'])

      onReset()
      fetchTagData()
      fetchData()

  }, [location.pathname, type]);

  useEffect(() => {
    DebugService.log(`Azure Inventory: On pagination change`)
    fetchData()
    // }, [paginationData, selectedAccount])
  }, [selectedAccount])

  useEffect(() => {
    if (refresh) {
      setTimeout(() => {
        DebugService.log(`Azure Inventory: Force refresh is called`)
        fetchData()
      }, 1000)
    }
  }, [refresh])

  const onFilterEnter = e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      fetchData()
    }
  }

  /**
   * @description Call fetchData on filter change
   * 
   * 1. User need to hit enter key if selectedFilter other than 'tags' is choosen
   * 2. Fetch data only when tag value list is selected in case of tag filter
   */
  useEffect(() => {
    if (filterData['selectedFilter'] === 'tags' && filterData.tagValues.length > 0) {
      fetchData()
    }
  }, [filterData])

  const isInstanceStopped = (instance) => {
    if (type === 'vm') {
      return (instance?.vmStatus ?? instance?.status ?? '').toLowerCase() === 'deallocated';
    }
    if (type === 'vmss') {
      return (instance?.currentCapacity ?? 1) === 0;
    }
    if (['mysql', 'postgresql', 'sql'].includes(type)) {
      return ['stopped', 'stopping'].includes((instance?.state ?? '').toLowerCase());
    }
    return false;
  };

  const isInstanceRunning = (instance) => !isInstanceStopped(instance);

  /**
   * Returns only the instances that should actually be processed for the given action
   */
  const getFilteredInstancesForAction = (action) => {
    if (!action || !selectedInstances.length) return [];

    if (action === 'START_NOW') {
      return selectedInstances.filter(isInstanceStopped);
    }
    if (['STOP_NOW', 'SCHEDULE_LATER', 'SCHEDULE_IDLE'].includes(action)) {
      return selectedInstances.filter(isInstanceRunning);
    }
    // For Scale actions - return all
    return selectedInstances;
  };

  /**
   * Instant Action Handler
   * @param {*} action 
   */
  const instantActionHandler = async (action, data) => {
    setShowLoading(true)
    setRefresh(false)

    const userDetails = getScopedItem(STORAGE_KEYS.LOCAL_USER_DETAILS)
    const parseToJson = JSON.parse(userDetails)

    const dataRequest = {
      userAccountId: '',
      userId: '',
      status: action === InstanceActionList.START ? InstanceActionList.START : InstanceActionList.STOP,
    }

    if (selectedAccount) {
      dataRequest['userAccountId'] = selectedAccount
    }

    if (parseToJson['userId']) {
      dataRequest['userId'] = parseToJson['userId']
    }

    const filteredData = data.filter(instance => {
      if (action === InstanceActionList.START) {
        return isInstanceStopped(instance);
      }
      if (action === InstanceActionList.STOP) {
        return isInstanceRunning(instance);
      }
      return true;
    });

    DebugService.log(`Selected instances for ${action}`, filteredData)

    if (filteredData.length === 0) {
      setActionStatusMessage(action === InstanceActionList.START ? 'No stopped instances selected to start.' : 'No running instances selected to stop.');
      setActionStatus('failed');
      setShowLoading(false);
      return;
    }

    if (filteredData.length > 0) {
      // Request body for VM
      if (type === 'vm') {
        dataRequest['vms'] = []
        for (let i = 0; i < filteredData.length; i++) {
          const instanceObj = {
            vmId: filteredData[i]['id'],
            location: filteredData[i]['location'],
            vmSize: filteredData[i]['vmSize'],
            vmName: filteredData[i]['name'],
            osType: filteredData[i]['osType'],
            resourceGroup: filteredData[i]['resourceGroup'],
          }

          dataRequest['vms'] = [
            ...dataRequest['vms'],
            instanceObj
          ]
        }
      }

      // Request body for db types
      if (['mysql', 'postgresql', 'sql'].includes(type)) {
        dataRequest['serviceType'] = AZURE_DB_TYPE[type] ?? ''
        dataRequest['databases'] = []
        for (let i = 0; i < filteredData.length; i++) {
          const instanceObj = {
            serverName: filteredData[i]['serverName'],
            skuTier: filteredData[i]['skuTier'],
            location: filteredData[i]['location'],
            resourceGroup: filteredData[i]['resourceGroup'],
          }

          if (type === 'sql') {
            instanceObj['skuFamily'] = filteredData[i]['skuFamily']
            instanceObj['vCore'] = filteredData[i]['vCores']
          } else {
            instanceObj['skuName'] = filteredData[i]['skuName']
          }

          dataRequest['databases'] = [
            ...dataRequest['databases'],
            instanceObj
          ]
        }
      }
    }

    // API Call
    if ((dataRequest['vms'] && dataRequest['vms'].length > 0) || (dataRequest['databases'] && dataRequest['databases'].length > 0)) {
      DebugService.log(`Current Instance Component: ${action} instances initiated`, dataRequest)

      let endpoint = APIConstants.ENDPOINT_AZURE_INVENTORY_VM_INSTANT_SCHEDULE_EVENT
      if (['mysql', 'postgresql', 'sql'].includes(type)) {
        endpoint = APIConstants.ENDPOINT_AZURE_INVENTORY_DB_INSTANT_SCHEDULE_EVENT
      }

      await InventoryApiService.azureInstantAction(
        endpoint,
        dataRequest
      ).then(res => {
        DebugService.log(`Azure Inventory Component: Instance ${dataRequest['status']} response`, res)
      
        if (res['data']?.[0]?.['error']) {
          setActionStatusMessage(res['data'][0]['error']?.['message'] ?? 'Something went wrong')
          setActionStatus('failed')
          return
        }
      
        // treat both success and error as success
        setTimeout(() => {
          setActionStatusMessage(null)
          setActionStatus(null)
          setRefresh(true)
        }, 1000)
      
        onReset()
        setActionStatusMessage(`Instance ${dataRequest['status']} requested successfully. It will take sometime to reflect.`)
        setActionStatus('success')
      
      }).catch(err => {
        DebugService.error(`Azure Inventory Component: Instance ${dataRequest['status']} failure`, err)
        setActionStatusMessage(`Instance ${dataRequest['status']} requested successfully. It will take sometime to reflect.`)
        setActionStatus('success')
        setTimeout(() => {
          setActionStatusMessage(null)
          setActionStatus(null)
          setRefresh(true)
        }, 1000)
      
      }).finally(() => {
        setShowLoading(false)
      })
    }
  };

  /**
   * Schedule later save handler
   */
  const onScheduleLaterSave = async ({
    startDate,
    stopDate,
    recurringOption,
    selectedDays,
    desireCount,
    minimumCount,
    timezone
  }) => {
    setShowLoading(true)

    let selectedRunningInstances = ''
    const userDetails = getScopedItem(STORAGE_KEYS.LOCAL_USER_DETAILS)
    const parseToJson = JSON.parse(userDetails)

    const dataRequest = {
      userAccountId: selectedAccount ?? '',
      userId: '',
      startInstanceTime: startDate,
      stopInstanceTime: stopDate,
      type: recurringOption,
    }

    if (parseToJson['userId']) {
      dataRequest['userId'] = parseToJson['userId']
    }

    if (recurringOption === 'WEEKLY') {
      dataRequest['days'] = selectedDays
      dataRequest['timezone'] = timezone
    }

    // Request body for VM
    if (type === 'vm') {
      dataRequest['vms'] = []

      selectedRunningInstances = selectedInstances.filter(isInstanceRunning)

      if (selectedRunningInstances.length === 0) {
        setShowLoading(false)
        setActionStatusMessage('No running instances selected to schedule.');
        setActionStatus('failed')
        return
      }

      for (let i = 0; i < selectedRunningInstances.length; i++) {
        const instanceObj = {
          vmId: selectedRunningInstances[i]['id'],
          location: selectedRunningInstances[i]['location'],
          vmSize: selectedRunningInstances[i]['vmSize'],
          vmName: selectedRunningInstances[i]['name'],
          osType: selectedRunningInstances[i]['osType'],
          resourceGroup: selectedRunningInstances[i]['resourceGroup'],
        }

        dataRequest['vms'] = [
          ...dataRequest['vms'],
          instanceObj
        ]
      }
    }

    // Request body for VMSS
    else if (type === 'vmss') {
      dataRequest['vmssList'] = []

      selectedRunningInstances = selectedInstances.filter(isInstanceRunning)

      if (selectedRunningInstances.length === 0) {
        setShowLoading(false)
        setActionStatusMessage('No running instances selected to schedule.');
        setActionStatus('failed')
        return
      }

      for (let i = 0; i < selectedRunningInstances.length; i++) {
        const instanceObj = {
          vmssName: selectedRunningInstances[i]['vmssName'],
          resourceGroup: selectedRunningInstances[i]['resourceGroup'],
          location: selectedRunningInstances[i]['location'],
          scalingMode: selectedRunningInstances[i]['scalingMode'],
          orchestrationMode: selectedRunningInstances[i]['orchestrationMode'],
          currentCapacity: selectedRunningInstances[i]['currentCapacity'],
          currentDefaultCapacity: selectedRunningInstances[i]['defaultCapacity'],
          currentMinCapacity: selectedRunningInstances[i]['minCapacity'],
          currentMaxCapacity: selectedRunningInstances[i]['maxCapacity'],
          capacity: desireCount,
          minCapacity: minimumCount,
          vmDetails: (selectedRunningInstances[i]['attachedVMs'] || []).map(vm => ({
            instanceId: vm['instanceId'],
            vmName: vm['vmName']
          }))
        }

        // Add tagValues for Autoscaling mode
        if (selectedRunningInstances[i]['scalingMode'] === 'Autoscaling') {
          instanceObj['tagValues'] = selectedRunningInstances[i]['tags'] || []
        }

        dataRequest['vmssList'] = [
          ...dataRequest['vmssList'],
          instanceObj
        ]
      }
    }

    // Request body for DB
    else if (['mysql', 'postgresql', 'sql'].includes(type)) {
      dataRequest['serviceType'] = AZURE_DB_TYPE[type] ?? ''
      dataRequest['databases'] = []
      selectedRunningInstances = selectedInstances.filter(isInstanceRunning)

      if (selectedRunningInstances.length === 0) {
        setShowLoading(false)
        setActionStatusMessage('No running instances selected to schedule.');
        setActionStatus('failed')
        return
      }

      for (let i = 0; i < selectedRunningInstances.length; i++) {
        const instanceObj = {
          serverName: selectedRunningInstances[i]['serverName'],
          resourceGroup: selectedRunningInstances[i]['resourceGroup'],
          skuTier: selectedRunningInstances[i]['skuTier'],
          location: selectedRunningInstances[i]['location'],
          skuName: selectedRunningInstances[i]['skuName']
        }

        if (type === 'sql') {
          instanceObj['skuFamily'] = selectedRunningInstances[i]['skuFamily']
          instanceObj['vCore'] = selectedRunningInstances[i]['vCores']
        }
        // } else {
        //   instanceObj['skuName'] = selectedRunningInstances[i]['skuName']
        // }

        dataRequest['databases'] = [
          ...dataRequest['databases'],
          instanceObj
        ]
      }
    }

    DebugService.log(`Azure Inventory Component: Schedule later initiated`, dataRequest)

    let endpoint = APIConstants.ENDPOINT_AZURE_INVENTORY_VM_SCHEDULE_EVENTS

    if (type === 'vmss') {
      endpoint = APIConstants.ENDPOINT_AZURE_INVENTORY_VMSS_SCHEDULE_EVENTS
    } else if (['mysql', 'postgresql', 'sql'].includes(type)) {
      endpoint = APIConstants.ENDPOINT_AZURE_INVENTORY_DB_SCHEDULE_EVENTS
    }

    await InventoryApiService.scheduleAzureEvents(
      endpoint,
      dataRequest
    ).then(res => {
      DebugService.log(`Azure Inventory Component: Schedule later response`, res)

      if (res['error']) {
        setActionStatusMessage(`${res['error']['message'] ?? 'Something went wrong'}`)
        setActionStatus('failed')
      } else {
        const errorList = res['data'].filter(d => d['error'] !== undefined).map(d => d['error'])
        if (errorList.length > 0) {
          setActionStatusMessage(errorList[0]['message'] ?? 'Something went wrong')
          setActionStatus('failed')
        } else {
          setTimeout(() => {
            setActionStatusMessage(null)
            setActionStatus(null)
            setRefresh(true)
          }, 10000)

          setOpendialog(false)
          setActionStatusMessage(`Schedule later requested successfully. It will take sometime to reflect.`)
          setActionStatus('success')
        }
      }

    }).catch(err => {
      DebugService.error(`Azure Inventory Component: Schedule later failure`, err)
      setActionStatusMessage(`Error occurred while scheduling later`)
      setActionStatus('failed')
    }).finally(() => {
      setShowLoading(false)
    })
  }

  /**
   * On Scaling handler
   */
  const onScaleSave = async ({ desireCount, minimumCount }) => {
    setShowLoading(true)
    const userDetails = getScopedItem(STORAGE_KEYS.LOCAL_USER_DETAILS)
    const parseToJson = JSON.parse(userDetails)

    const dataRequest = {
      userAccountId: selectedAccount ?? '',
      userId: '',
      vmssList: []
    }

    if (parseToJson['userId']) {
      dataRequest['userId'] = parseToJson['userId']
    }

    if (selectedInstances.length > 0) {
      for (let i = 0; i < selectedInstances.length; i++) {
        const vmssObj = {
          vmssName: selectedInstances[i]['vmssName'],
          resourceGroup: selectedInstances[i]['resourceGroup'],
          location: selectedInstances[i]['location'],
          scalingMode: selectedInstances[i]['scalingMode'],
          orchestrationMode: selectedInstances[i]['orchestrationMode'],
          currentCapacity: selectedInstances[i]['currentCapacity'],
          currentDefaultCapacity: selectedInstances[i]['defaultCapacity'],
          currentMinCapacity: selectedInstances[i]['minCapacity'],
          currentMaxCapacity: selectedInstances[i]['maxCapacity'],
          capacity: desireCount,
          minCapacity: minimumCount,
          vmDetails: (selectedInstances[i]['attachedVMs'] || []).map(vm => ({
            instanceId: vm['instanceId'],
            vmName: vm['vmName']
          }))
        }

        // Add tagValues for Autoscaling mode
        if (selectedInstances[i]['scalingMode'] === 'Autoscaling') {
          vmssObj['tagValues'] = selectedInstances[i]['tags'] || []
        }

        dataRequest['vmssList'] = [...dataRequest['vmssList'], vmssObj]
      }
    }

    DebugService.log(`Azure Inventory Component: Scaling initiated`, dataRequest)

    await InventoryApiService.scheduleAzureEvents(
      APIConstants.ENDPOINT_AZURE_INVENTORY_VMSS_INSTANT_SCHEDULE_EVENT,
      dataRequest
    ).then(res => {
      DebugService.log(`Azure Inventory Component: Scaling response`, res)

      if (res['error']) {
        setActionStatusMessage(res['error']['message'] ?? 'Something went wrong')
        setActionStatus('failed')
      } else {
        const errorList = res['data'].filter(d => d['error'] !== undefined).map(d => d['error'])
        if (errorList.length > 0) {
          setActionStatusMessage(errorList[0]['message'] ?? 'Something went wrong')
          setActionStatus('failed')
        } else {
          setTimeout(() => {
            setActionStatusMessage(null)
            setActionStatus(null)
            setRefresh(true)
          }, 10000)

          setOpenModifyASGDialog(false)
          setActionStatusMessage(`Scaled successfully`)
          setActionStatus('success')
        }
      }

    }).catch(err => {
      DebugService.error(`Azure Inventory Component: Scaled failure`, err)
      setActionStatusMessage(`Error occurred while scaling`)
      setActionStatus('failed')
    }).finally(() => {
      setOpendialog(false)
      setShowLoading(false)
    })
  }

  /**
   * Schedule idle handler
   */
  const onScheduleIdleSave = async ({ idleDuration }) => {
    setShowLoading(true)
    const userDetails = getScopedItem(STORAGE_KEYS.LOCAL_USER_DETAILS)
    const parseToJson = JSON.parse(userDetails)

    const dataRequest = {
      userAccountId: selectedAccount ?? '',
      userId: '',
    }

    if (parseToJson['userId']) {
      dataRequest['userId'] = parseToJson['userId']
    }

    // VM
    const runningInstances = selectedInstances.filter(isInstanceRunning)
    if (runningInstances.length === 0) {
      setShowLoading(false)
      setActionStatusMessage('No running instances selected to schedule idle.');
      setActionStatus('failed')
      return
    }

    dataRequest['instances'] = []
    for (let i = 0; i < runningInstances.length; i++) {
      const instance = {
        instanceId: runningInstances[i]['id'],
        instanceName: runningInstances[i]['name'],
        region: runningInstances[i]['location'],
        instanceType: runningInstances[i]['vmSize'],
        idleDays: idleDuration,
        osType: runningInstances[i]['osType'],
        resourceGroup: runningInstances[i]['resourceGroup']
      }

      dataRequest['instances'] = [...dataRequest['instances'], instance]
    }
    // }

    DebugService.log(`Azure Inventory Component: Request to schedule idle initiated`, dataRequest)

    let endpoint = APIConstants.ENDPOINT_AZURE_INVENTORY_VM_SCHEDULE_IDLE_SAVE

    // if (type === 'vmss') {
    //   endpoint = APIConstants.ENDPOINT_AZURE_INVENTORY_VMSS_SCHEDULE_IDLE_SAVE
    // }

    await InventoryApiService.saveAzureScheduleIdle(
      endpoint,
      dataRequest
    ).then(res => {
      DebugService.log(`Azure Inventory Component: Schedule Idle response`, res)

      if (res['error']) {
        setActionStatusMessage(res['error']['message'] ?? 'Something went wrong')
        setActionStatus('failed')
      } else {
        const errorList = res['data'].filter(d => d['error'] !== undefined).map(d => d['error'])
        if (errorList.length > 0) {
          setActionStatusMessage(errorList[0]['message'] ?? 'Something went wrong')
          setActionStatus('failed')
        } else {
          setTimeout(() => {
            setActionStatusMessage(null)
            setActionStatus(null)
            setRefresh(true)
          }, 10000)

          setOpenScheduleIdleDialog(false)
          setActionStatusMessage(`Instances scheduled successfully`)
          setActionStatus('success')
        }
      }

    }).catch(err => {
      DebugService.error(`Azure Inventory Component: Schedule Idle failure`, err)
      setActionStatusMessage(`Error occurred while scheduling idle`)
      setActionStatus('failed')
    }).finally(() => {
      setOpendialog(false)
      setShowLoading(false)
    })
  }

  /**
   * On Instances table row checkbox click handler
   * Fixed version - properly keeps row selection across pages
   * @param {*} newSelectedIDList 
   */
  const onDataGridCheckboxSelectHandler = useCallback((newSelectedIDList = []) => {
    setSelectedInstances(prevSelected => {
      const prevMap = new Map(prevSelected.map(item => [item.id, item]));
      const currentPageIds = new Set(rows.map(r => r.id));

      // Remove items deselected on current page
      for (const id of currentPageIds) {
        if (!newSelectedIDList.includes(id)) {
          prevMap.delete(id);
        }
      }

      // Add currently selected items from current page
      const newlySelectedRows = rows.filter(r => newSelectedIDList.includes(r.id));
      for (const row of newlySelectedRows) {
        prevMap.set(row.id, row);
      }

      return Array.from(prevMap.values());
    });
  }, [rows])

  /**
   * Select action handler
   * @param {*} actionCase 
   */
  const onTakeActionHandler = (actionCase) => {
    // Guard disabled actions
    if (disabledActions.has(actionCase)) {
      const messages = {
        START_NOW:      'Start Now is not available: no selected instance is stopped/deallocated.',
        STOP_NOW:       'Stop Now is not available: no selected instance is running.',
        SCHEDULE_LATER: 'Schedule Shutdown is not available: no selected instance is running.',
        SCHEDULE_IDLE:  'Schedule Idle Shutdown is not available: no selected instance is running.',
        SCALE_DOWN:     'Scale Down is not available: all selected scale sets are already at capacity 0.',
        SCALE_DEFAULT:  'Scale Default is not available: all selected scale sets are already running.',
      };
      setActionStatusMessage(messages[actionCase] ?? 'This action is not available for the current state.');
      setActionStatus('failed');
      return;
    }

    setShowLoading(true)
    setOpendialog(false);
    // setOpenModifyASGDialog(false);
    setActionStatus(null)
    setActionStatusMessage(null)
    setInstanceAction(actionCase)

    if (selectedInstances.length === 0) {
      setActionStatusMessage('Select one or more instances')
      setActionStatus('failed')
      setShowLoading(false)
    } else {

      // When start now option is choosen
      if (actionCase === 'START_NOW') {
        // const selectedStoppedInstances = selectedInstances.filter(i => i['instanceState'] === 'stopped' || i['Status'] === 'stopped')

        // if (selectedStoppedInstances.length === 0) {
        //   setActionStatusMessage('Select one or more stopped instances')
        //   setActionStatus('failed')
        //   setShowLoading(false)

        // } else {
        // setSelectedAction('started')
        setShowConfirmModal(true)
        setShowLoading(false)
        // }

        // When stop now option is choosen
      } else if (actionCase === 'STOP_NOW') {
        // const selectedRunningInstances = selectedInstances.filter(i => i['instanceState'] === 'running' || i['Status'] === 'available')

        // if (selectedRunningInstances.length === 0) {
        //   setActionStatusMessage('Select one or more running/available instances')
        //   setActionStatus('failed')
        //   setShowLoading(false)
        // } else {
        // setSelectedAction('stopped')
        setShowConfirmModal(true)
        setShowLoading(false)
        // }

        // When schedule later option is choosen
        // Schedule later is specific for stop action
      } else if (actionCase === 'SCHEDULE_LATER') {
        DebugService.log(`Azure Inventory Component: Schedule later requested`)

        // ASG
        // if (type === 'asg') {
        setShowLoading(false)
        // setSelectedAction('will be scheduled')
        setShowConfirmModal(true)
        // } else {
        //   // RDS & EC2
        //   const selectedRunningInstances = selectedInstances.filter(i => i['instanceState'] === 'running' || i['Status'] === 'available')

        //   if (selectedRunningInstances.length === 0) {
        //     setActionStatusMessage('Select one or more running/available instances')
        //     setActionStatus('failed')
        //     setShowLoading(false)
        //   } else {
        //     setShowLoading(false)
        //     // setSelectedAction('will be scheduled')
        //     setShowConfirmModal(true)
        //   }
        // }
      } else if ([
        'SCALE_DOWN',
        'SCALE_DEFAULT',
        'SCALE_DEFAULT_ASG',
        'SCHEDULE_IDLE'
      ].includes(actionCase)) {
        setShowLoading(false)
        // setSelectedAction('will be scaled')
        setShowConfirmModal(true)

      }
    }
  }

  // const getActionMessageForConfirmModal = () => {
  //   switch (instanceAction) {
  //     case 'START_NOW': return 'started'
  //     case 'STOP_NOW': return 'stopped'
  //     // case 'SCHEDULE_LATER': return 'scheduled'
  //     // case 'SCALE_DOWN_ASG':
  //     // case 'SCALE_DEFAULT_ASG': return 'scaled'
  //     case 'SCHEDULE_IDLE': return 'set Idle'
  //     default: return ''
  //   }
  // }

  const getActionMessageForConfirmModal = () => {
  switch (instanceAction) {
    case 'START_NOW':      return 'started'
    case 'STOP_NOW':       return 'stopped'
    case 'SCHEDULE_LATER': return 'scheduled for shutdown'
    case 'SCALE_DOWN':     return 'scaled down'
    case 'SCALE_DEFAULT':  return 'restored to default scale'
    case 'SCALE_DEFAULT_ASG':  return 'restored to default scale'
    case 'SCHEDULE_IDLE':  return 'set for idle shutdown'
    default:               return ''
  }
}

  /**
   * Action confirmation handler
   */
  const onConfirmModalAction = (isConfirm) => {
    setShowConfirmModal(false);

    if (!isConfirm) {
      onReset();
      setShowLoading(false);
      return;
    }

    const filteredInstances = getFilteredInstancesForAction(instanceAction);

    if (filteredInstances.length === 0) {
      setActionStatusMessage('No valid instances for this action.');
      setActionStatus('failed');
      setShowLoading(false);
      return;
    }

    // Show info if some instances were filtered out
    if (filteredInstances.length !== selectedInstances.length) {
      const skipped = selectedInstances.length - filteredInstances.length;
      setActionStatusMessage(`${skipped} instance(s) were skipped as they are already in the target state.`);
      setActionStatus('success'); // or 'info' if you have that theme
    }

    setShowLoading(true);

    if (instanceAction === 'START_NOW') {
      instantActionHandler(InstanceActionList.START, filteredInstances);

    } else if (instanceAction === 'STOP_NOW') {
      instantActionHandler(InstanceActionList.STOP, filteredInstances);

    } else if (instanceAction === 'SCALE_DOWN') {
      setOpenModifyASGDialog(true);
      setShowLoading(false);

    } else if (instanceAction === 'SCALE_DEFAULT') {
      onScaleSave({
        desireCount: selectedInstances[0]?.['currentCapacity'] ?? null,
        minimumCount: selectedInstances[0]?.['minCapacity'] ?? null
      });

    } else if (instanceAction === 'SCHEDULE_IDLE') {
      setOpenScheduleIdleDialog(true);
      setShowLoading(false);

    } else {
      // SCHEDULE_LATER
      setOpendialog(true);
      setShowLoading(false);
    }
  };

  /**
   * Selected instances reset handler
   */
  const onReset = () => {
    setActionStatus(null)
    setActionStatusMessage(null)
    setSelectedInstances([])
    // setSelectedAction('')
    setShowConfirmModal(false)

    setActionBy('DROPDOWN')
    setInstanceAction(null)

    setFilterData({
      selectedFilter: '',
      filterValue: '',
      tagKey: '',
      tagValues: []
    })
  }

  /**
   * Refresh button click handler
   */
  const onRefreshClick = () => {
    if (showLoading || refresh) return;
    setFilterData({
      selectedFilter: '',
      filterValue: '',
      tagKey: '',
      tagValues: []
    })
    setActionStatus(null)
    setActionStatusMessage(null)
    setRefresh(true)
  }


  /**
  * @description Instant subscription activation handler
  */
  const activateSubscription = useCallback(() => {
    setActionStatusMessage(null)
    setActionStatus(null)

    const userSubscriptionId = userDetails?.['userSubscriptionId']
    if (userSubscriptionId) {
      const userDetails = getScopedItem(STORAGE_KEYS.LOCAL_USER_DETAILS)
      const parseToJson = JSON.parse(userDetails)

      const requestBody = {
        userId: parseToJson?.['userId'],
        accountId: selectedAccount,
        userSubscriptionId: userSubscriptionId
      }

      DebugService.log(`Optimization Component: Activate subscription request initiated`, requestBody)

      SubscriptionApiService.activateSubscription(requestBody)
        .then(res => {
          DebugService.log(`Optimization Component: Activate subscription response`, res)

          if (res['data']) {
            setActionStatus('success')
            setActionStatusMessage(`${res['data']['message']}. Reload your page`)
            refetchUserDetails()
          } else {
            if (res['error']['code'] === 525) {
              setScopedItem(STORAGE_KEYS.CURRENT_ROUTE, CONST_ROUTE_PATH.PRICING)
              navigate(CONST_ROUTE_PATH.PRICING)
            } else {
              setActionStatus('failed')
              setActionStatusMessage(res['error']['message'] ?? 'Subscription activation failed')
            }
          }
        })
        .catch(error => {
          DebugService.log(`Optimization Component: Plan purchase failed`, error)
          setActionStatus('failed')
          setActionStatusMessage('Something went wrong')
        })
    }
  }, [userDetails])

  /**
   * @description Activate Now element
   * @returns 
   */
  const activateNowEl = () => {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          width: '100%',
          marginLeft: '1vw'
        }}
      >
        <Typography
          // variant='subtitle1'
          color={'#33adff'}
          style={{
            fontSize: '14px'
          }}
        >
          See only {TRIAL_SUBSCRIPTION_DATA_LIMIT} records?&nbsp;
          <Button
            variant="contained"
            size="small"
            // className='activate-button'
            title='Click to activate'
            onClick={() => setShowActivateNowConfirmModal(true)}
            style={{
              // color: '#ff6600',
              textTransform: 'none',
              padding: '3px',
              lineHeight: '1.2',
              backgroundColor: '#ff6600'
            }}
          >
            Activate Now
          </Button>
          {/* <Link className="navigate-link"
            title="Click to activate current subscription"
            // onClick={() => activateSubscription()}
            style={{
              // fontSize: '10px',
              // textDecoration: 'none',
              color: '#ff6600',
              fontWeight: '600'
            }}
            href="#">
            Activate Now
          </Link> */}
          &nbsp;to access full data.
        </Typography>
        {/* <Typography
          variant="h6"
          color="textSecondary"
        // className='activate-subscription-text'
        >
          <Link
            variant="contained"
            size="small"
            // className='activate-button'
            title='Click to activate'
            onClick={() => navigate(CONST_ROUTE_PATH.PRICING)}
          >
            Activate Now
          </Button>
        </Typography> */}
      </Box>
    )
  }

  /**
   * @description Activate now confirmation handler
   * @param {*} isConfirm 
   */
  const activateNowConfirmationHandler = (isConfirm) => {
    if (isConfirm) {
      activateSubscription()
    }

    setShowActivateNowConfirmModal(false)
  }

  /**
   * @description Action from column handler
   * @param {*} actionCase 
   * @param {*} instanceID 
   */
  const handleGridColumnAction = (actionCase, instanceID) => {
    // DebugService.log(`Current Instance Component: Action column is called`, actionCase, instanceID, rows)
    if (instanceID) {
      setActionBy('GRIDCELL')
      setInstanceAction(actionCase)
      onDataGridCheckboxSelectHandler([instanceID])
    }
  }

  /**
   * @description UseEffect called for Action Column
   */
  useEffect(() => {
    if (actionBy === 'GRIDCELL' && selectedInstances.length > 0 && instanceAction !== null) {
      onTakeActionHandler(instanceAction)
    }
  }, [selectedInstances, actionBy, instanceAction, rows])

  // Filter change handlers
  const onFilterChange = useCallback((fieldName, value) => {
    setFilterData(prev => ({
      ...prev,
      [fieldName]: value,
      // Reset dependent fields when filter type changes
      ...(fieldName === 'selectedFilter' && {
        tagKey: '',
        tagValues: [],
        filterValue: ''
      }),
      // Reset tag values when tag key changes
      ...(fieldName === 'tagKey' && {
        tagValues: []
      })
    }))
  }, []);

  /**
  * @description Stores the latest API response in storage.
  * @author Ashish Sharma
  */
  useEffect(() => {
    const prev = getScopedItem(STORAGE_KEYS.AI_CHATBOT_REQUEST_DATA) || {};
    setScopedItem(STORAGE_KEYS.AI_CHATBOT_REQUEST_DATA, {
      ...prev,
      instances: currentInstanceApiData
    });
  }, [currentInstanceApiData]);

  const onVMSSCheckboxChange = (row, isChecked) => {
    setSelectedInstances((prev) => {
      if (isChecked) {
        if (prev.find((i) => i['id'] === row['id'])) return prev;
        return [...prev, row];
      } else {
        return prev.filter((i) => i['id'] !== row['id']);
      }
    });
  };
  
  const disabledActions = getDisabledActions(selectedInstances, type);

  // ── Selection wiring for the new-design DB table ──────────────────────
  // Drives the same `selectedInstances` state the action handlers read, so
  // Start/Stop/Schedule keep working unchanged.
  const selectedIdSet = new Set(selectedInstances.map((i) => i.id));
  const allRowsSelected = rows.length > 0 && rows.every((r) => selectedIdSet.has(r.id));

  const toggleRowSelection = (row) => {
    setSelectedInstances((prev) =>
      prev.some((i) => i.id === row.id) ? prev.filter((i) => i.id !== row.id) : [...prev, row]
    );
  };

  const toggleAllRowsSelection = (checked) => {
    setSelectedInstances((prev) => {
      if (checked) {
        const map = new Map(prev.map((i) => [i.id, i]));
        rows.forEach((r) => map.set(r.id, r));
        return Array.from(map.values());
      }
      const pageIds = new Set(rows.map((r) => r.id));
      return prev.filter((i) => !pageIds.has(i.id));
    });
  };

  const dbCheckboxColumn = {
    key: 'checkbox',
    label: (
      <input
        type="checkbox"
        checked={allRowsSelected}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => toggleAllRowsSelection(e.target.checked)}
      />
    ),
    render: (row) => (
      <input
        type="checkbox"
        checked={selectedIdSet.has(row.id)}
        onClick={(e) => e.stopPropagation()}
        onChange={() => toggleRowSelection(row)}
      />
    ),
  };

  return (
    <Grid container spacing={1} className='inventory-subpage'>
      {/* Table Loader */}
      {showLoading && <TableLoader />}

      {/* Content */}
      {!showLoading && (
        <>
          {/* <Grid item xs={12} md={6} lg={4}>
            <BlockContainer
              order={1}
              displayIcon={BLOCK_CONTAINER_ICON.INVENTORY_INSTANCE}
              displayValue={totalInstancesCount}
              displayLabel={type === 'asg' ? 'Total ASG' : 'Total Instances'}
              displayBGColor={'#D7BDE2'}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <BlockContainer
              order={3}
              displayIcon={BLOCK_CONTAINER_ICON.INVENTORY_INSTANCE}
              displayValue={totalRunningInstancesCount}
              displayLabel={'Total Running Instances'}
              displayBGColor={'#f9ba9f'}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <BlockContainer
              order={5}
              displayIcon={BLOCK_CONTAINER_ICON.INVENTORY_INSTANCE}
              displayValue={totalStoppedInstancesCount}
              displayLabel={type === 'asg' ? 'Total Unused ASG' : 'Total Stopped Instances'}
              displayBGColor={'#F9E79F'}
            />
          </Grid> */}

          <Grid item xs={12}>
            {/* Filter + action toolbar (new design) */}
            <div className="filter-bar">
              <div className="field">
                <label>Filter</label>
                <select
                  value={filterData.selectedFilter}
                  onChange={(e) => onFilterChange('selectedFilter', e.target.value)}
                >
                  <option value="">Select Filter</option>
                  {(type === 'vmss' ? vmssFilterOptions : type === 'vm' ? vmFilterOptions : dbFilterOptions)
                    .map((option, index) => (
                      <option key={index} value={option.value}>{option.label}</option>
                    ))}
                </select>
              </div>

              {filterData.selectedFilter === 'tags' && (
                <>
                  <div className="field">
                    <label>Tag Key</label>
                    <select
                      value={filterData.tagKey?.Key ?? ''}
                      onChange={(e) => {
                        const key = filterTagList.find((f) => f['Key'] === e.target.value) ?? '';
                        onFilterChange('tagKey', key);
                      }}
                    >
                      <option value="">Select Key</option>
                      {filterTagList.map((f, i) => (<option key={i} value={f['Key']}>{f['Key']}</option>))}
                    </select>
                  </div>
                  <div className="field">
                    <label>Tag Values</label>
                    <MultiSelect
                      options={filterData.tagKey?.Value ?? []}
                      value={filterData.tagValues}
                      onChange={(vals) => onFilterChange('tagValues', vals)}
                      disabled={!filterData.tagKey}
                    />
                  </div>
                </>
              )}

              {filterData.selectedFilter && filterData.selectedFilter !== 'tags' && (
                <div className="field">
                  <label>Filter Value</label>
                  <input
                    type="text"
                    placeholder="Enter filter value"
                    value={filterData.filterValue}
                    onChange={(e) => onFilterChange('filterValue', e.target.value)}
                    onKeyDown={onFilterEnter}
                  />
                </div>
              )}

              <div className="field">
                <label>Action</label>
                <select
                  value={'SELECT_ACTION'}
                  onChange={(e) => onTakeActionHandler(e.target.value)}
                  disabled={selectedInstances.length === 0 || currentUserAccess === USER_ROLE.READ_ONLY}
                >
                  <option value="SELECT_ACTION">Select Action</option>
                  {!['vmss'].includes(type) && <option value="START_NOW" disabled={disabledActions.has('START_NOW')}>Start Now</option>}
                  {!['vmss'].includes(type) && <option value="STOP_NOW" disabled={disabledActions.has('STOP_NOW')}>Stop Now</option>}
                  {['vmss'].includes(type) && <option value="SCALE_DOWN" disabled={disabledActions.has('SCALE_DOWN')}>Scale Down</option>}
                  {['vmss'].includes(type) && <option value="SCALE_DEFAULT" disabled={disabledActions.has('SCALE_DEFAULT')}>Scale Default</option>}
                  {['vm'].includes(type) && <option value="SCHEDULE_IDLE" disabled={disabledActions.has('SCHEDULE_IDLE')}>Schedule Idle Shutdown</option>}
                  <option value="SCHEDULE_LATER" disabled={disabledActions.has('SCHEDULE_LATER')}>Schedule Shutdown</option>
                </select>
              </div>

              <div className="spacer" />
              <button className="btn ghost" onClick={() => onRefreshClick()} disabled={showLoading || refresh}>↻ {(showLoading || refresh) ? 'Refreshing…' : 'Refresh'}</button>
            </div>
          </Grid>

          <Grid item xs={12}>
            {type === 'vmss' ? (
              <VMSSInventoryTable
                rows={rows}
                selectedIDList={(selectedInstances ?? []).map((d) => d['id'])}
                onCheckboxChange={onVMSSCheckboxChange}
                totalCount={rows.length}
                pageSize={25}
                currentPage={1}
                onPageChange={() => {}}
                onPageSizeChange={() => {}}
              />
            ) : ['mysql', 'postgresql', 'sql'].includes(type) ? (
              <NewTable
                rows={rows}
                columns={[dbCheckboxColumn, ...dbNewColumns]}
                loading={false}
                pageSizes={[10, 25, 50, 100]}
                defaultPageSize={25}
                emptyText="No database instances found."
              />
            ) : (
              <NewTable
                rows={rows}
                columns={[dbCheckboxColumn, ...vmNewColumns]}
                loading={false}
                pageSizes={[10, 25, 50, 100]}
                defaultPageSize={25}
                emptyText="No virtual machines found."
              />
           )}  
          </Grid>

          {/* Confirm Modal */}
          {showActivateNowConfirmModal && <ConfirmModal
            open={true}
            headingLabel={'Activate Your Subscription!'}
            message={[
              <p>By clicking the button below, you'll gain full access to premium features, exclusive content, and personalized recommendations.</p>,
              <br />
            ]}
            continueBtnLabel={'Proceed'}
            cancelBtnLabel={'Close'}
            onActionTaken={(isConfirm) => activateNowConfirmationHandler(isConfirm)}
          />}

          {/* Schedule later modal */}
          {Opendialog && <InstanceScheduler
            style={{ textTransform: 'none' }}
            open={true}
            handleClose={() => setOpendialog(false)}
            onSave={onScheduleLaterSave}
            instanceData={selectedInstances}
          />}

          {/* Scale modal */}
          {openModifyASGDialog && <ModifyASG
            style={{ textTransform: 'none' }}
            open={true}
            handleClose={() => setOpenModifyASGDialog(false)}
            onSave={onScaleSave}
            instanceData={selectedInstances}
          />}

          {/* Schedule Idle Modal */}
          {openScheduleIdleDialog && <ScheduleIdle
            style={{ textTransform: 'none' }}
            open={true}
            handleClose={() => setOpenScheduleIdleDialog(false)}
            onSave={onScheduleIdleSave}
            instanceData={selectedInstances}
          />}

          {/* Show action completion notification */}
          {actionStatus !== null && <SnackbarNotification
            initialOpen={true}
            duration={7000}
            message={actionStatusMessage}
            actionButtonName={'Ok'}
            theme={actionStatus === 'success' ? SNACKBAR_THEME.GREEN : SNACKBAR_THEME.RED}
            yPosition={'top'}
            xPosition={'center'}
          />}

          {/* Confirm Modal */}
          {showConfirmModal && <ConfirmModal
            open={true}
            message={[
              <Typography variant='subtitle2' color={'#000000'}>Following instances will be {getActionMessageForConfirmModal()}</Typography>,
              <Divider className='divider-custom' />,
              getFilteredInstancesForAction(instanceAction).map((instance, i) => (
                <Typography variant='body2' key={instance['id']}>
                  {instance['id']}
                  &nbsp;
                  {instance['name'] ? `(${instance['name']})` : ''}
                </Typography>
              ))
            ]}
            onActionTaken={(isConfirm) => onConfirmModalAction(isConfirm)}
          />}
        </>
      )}
    </Grid>
  )
};

/**
 * Scheduled events
 * @returns 
 */
export function ScheduledEvents() {

  const [data, setData] = useState([])
  const [rawData, setRawData] = useState([])
  const [showLoader, setShowLoader] = useState(false)
  const [selectedIDList, setSelectedIDList] = useState([])
  const [snackbarMessage, setSnackbarMessage] = useState(null)
  const [snackbarCase, setSnackbarCase] = useState(null)
  // const [isLoading, setIsLoading] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [isDeleteRecurring, setIsDeleteRecurring] = useState(false)
  const [currentUserAccess, setCurrentUserAccess] = useState(null)

  const [scheduledEventsApiData, setScheduledEventsApiData] = useState({})

  const location = useLocation()
  const { type } = useCurrentInstanceType()

  const { selectedAccount } = useAuth()
  const { getScopedItem } = useScopedLocalStorage()

  const [selectedTimezone, setSelectedTimezone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone,
  )

  /**
   * Instance(s) checkbox click handler
   * @param {*} selectedIDListVal 
   */
  const onDataGridCheckboxSelectHandler = (selectedIDListVal = []) => {
    DebugService.log(`Upcoming Schedules Component: selected instance id list`, selectedIDListVal)
    setSelectedIDList(selectedIDListVal)
  }

  /**
   * Delete button click handler
   */
  const onDeleteClick = () => {
    setShowLoader(true)
    setSnackbarMessage(null)
    setSnackbarCase(null)

    if (selectedIDList.length > 0) {
      let endpoint = APIConstants.ENDPOINT_AZURE_INVENTORY_VM_DELETE_SCHEDULED_EVENT

      const dataRequest = {
        userAccountId: selectedAccount ?? '',
      }

      if (type === 'vmss') {
        endpoint = APIConstants.ENDPOINT_AZURE_INVENTORY_VMSS_DELETE_SCHEDULED_EVENT
      } else if (['mysql', 'postgresql', 'sql'].includes(type)) {
        endpoint = APIConstants.ENDPOINT_AZURE_INVENTORY_DB_DELETE_SCHEDULED_EVENT
        dataRequest['serviceType'] = AZURE_DB_TYPE[type] ?? ''
      }

      if (isDeleteRecurring) {
        dataRequest['scheduleGroupIds'] = data.filter(d => selectedIDList.includes(d['id'])).map(d => d['scheduleGroupId'])
      } else {
        dataRequest['ids'] = selectedIDList
      }

      DebugService.log(`Upcoming Azure Schedule Component: Delete request with recurring option as ${isDeleteRecurring} is initiated`, dataRequest)

      InventoryApiService.deleteScheduledAzureEvents(endpoint, dataRequest)
        .then(res => {
          DebugService.log('Upcoming Azure Schedule Component: Data', res)

          if (res['data']) {
            setSnackbarCase('success')
            setSnackbarMessage(`Scheduled instances deleted successfully`)

            setTimeout(() => {
              fetchData()
            }, 1000)
          }

          if (res['error']) {
            setSnackbarCase('failed')
            setSnackbarMessage(res['error']?.['message'])
          }

          setShowLoader(false)
        })
        .catch(err => {
          DebugService.error('Upcoming Azure Schedule Component: Error caught while deleting data', err)
          setSnackbarCase('error')
          setSnackbarMessage(`Couldn't delete Scheduled instances due to some error`)
          setShowLoader(false)
        })
    }
  }

  const processData = (data) => {
    return data.map(d => {
      let startDateTime = d['startTime']
      let stopDateTime = d['stopTime']

      if (selectedTimezone?.['value'] && d['startTime']) {
        startDateTime = new Date(d['startTime']).toLocaleString('en-US', {
          timeZone: selectedTimezone['value']
        })
      }

      if (selectedTimezone?.['value'] && d['stopTime']) {
        stopDateTime = new Date(d['stopTime']).toLocaleString('en-US', {
          timeZone: selectedTimezone['value']
        })
      }

      return {
        ...d,
        startTime: format(startDateTime, 'dd-MM-yyyy HH:mm:ss'),
        stopTime: format(stopDateTime, 'dd-MM-yyyy HH:mm:ss')
      }
    })
  }

  /**
   * Fetch scheduled event list
   */
  const fetchData = async () => {
    setShowLoader(true);

    try {
      setData([])
      let endpoint = APIConstants.ENDPOINT_AZURE_INVENTORY_VM_SCHEDULED_EVENT_LIST
      const dbType = AZURE_DB_TYPE[type] ?? ''

      if (type === 'vmss') {
        endpoint = APIConstants.ENDPOINT_AZURE_INVENTORY_VMSS_SCHEDULED_EVENT_LIST
      } else if (['mysql', 'postgresql', 'sql'].includes(type)) {
        endpoint = APIConstants.ENDPOINT_AZURE_INVENTORY_DB_SCHEDULED_EVENT_LIST
      }

      InventoryApiService.getScheduledAzureEvents(selectedAccount, endpoint, dbType)
        .then(res => {
          DebugService.log('Upcoming Azure Schedule Component: Data', res)

          setScheduledEventsApiData(res?.data ? res : {})
          setRawData(res['data'] || [])
          setData(processData(res['data'] ?? []))

          setShowLoader(false);
        })
        .catch(err => {
          setShowLoader(false);
          DebugService.error('Upcoming Azure Schedule Component: Error caught while fetching data', err)
        })

    } catch (error) {
      setShowLoader(false);
      DebugService.error('Upcoming Azure Schedule Component: Fetch data error', error)
    } finally {
    }
  }

  const onConfirmModalAction = (isConfirm) => {
    setShowConfirmModal(false)

    if (isConfirm) {
      onDeleteClick()
    }
  }

  useEffect(() => {
    fetchData()

    const userDetails = getScopedItem(STORAGE_KEYS.LOCAL_USER_DETAILS)
    const parseToJson = JSON.parse(userDetails)
    setCurrentUserAccess(parseToJson['roleName'])
  }, [location.pathname, selectedAccount])

  useEffect(() => {
    setShowLoader(true)
    setData(prev => processData(rawData ?? []))
    setShowLoader(false)
  }, [selectedTimezone])

  /**
  * @description Stores the latest API response in storage.
  * @author Ashish Sharma
  */
  useEffect(() => {
    const prev = getScopedItem(STORAGE_KEYS.AI_CHATBOT_REQUEST_DATA) || {};
    setScopedItem(STORAGE_KEYS.AI_CHATBOT_REQUEST_DATA, {
      ...prev,
      scheduledEvents: scheduledEventsApiData
    });
  }, [scheduledEventsApiData]);

  // ── Selection wiring for the new-design Scheduled Events table ──────────
  const schedSelectedSet = new Set(selectedIDList);
  const allSchedSelected = data.length > 0 && data.every((r) => schedSelectedSet.has(r.id));
  const toggleSchedRow = (row) =>
    setSelectedIDList((prev) => (prev.includes(row.id) ? prev.filter((id) => id !== row.id) : [...prev, row.id]));
  const toggleSchedAll = (checked) => setSelectedIDList(checked ? data.map((r) => r.id) : []);
  const schedCheckboxColumn = {
    key: 'checkbox',
    label: (
      <input type="checkbox" checked={allSchedSelected} onClick={(e) => e.stopPropagation()} onChange={(e) => toggleSchedAll(e.target.checked)} />
    ),
    render: (row) => (
      <input type="checkbox" checked={schedSelectedSet.has(row.id)} onClick={(e) => e.stopPropagation()} onChange={() => toggleSchedRow(row)} />
    ),
  };

  return (
    <Grid container className='inventory-subpage'>
      {/* Table Loader */}
      {showLoader && <TableLoader />}

      {/* Content */}
      {!showLoader && (
        <>
          <Grid item xs={12} md={2}></Grid>
          <Grid item xs={12} md={8}>
            <TimezoneSelect value={selectedTimezone} onChange={(v) => {
              setSelectedTimezone(v)
            }} />
          </Grid>
          <Grid item xs={12} md={2}></Grid>
          <Grid item xs={12} md={10}></Grid>
          <Grid item xs={12} md={2} style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              className="btn ghost"
              title="Delete"
              disabled={!selectedIDList.length || currentUserAccess === USER_ROLE.READ_ONLY}
              onClick={() => setShowConfirmModal(true)}
            >
              🗑 Delete
            </button>
          </Grid>

          <Grid item xs={12}>
            <NewTable
              rows={data}
              columns={[schedCheckboxColumn, ...scheduledEventNewColumns]}
              loading={false}
              pageSizes={[10, 25, 50, 100]}
              defaultPageSize={25}
              emptyText="No scheduled events found."
            />
          </Grid>

          {snackbarMessage !== null && <SnackbarNotification
            initialOpen={true}
            duration={7000}
            message={snackbarMessage}
            actionButtonName={'Ok'}
            theme={snackbarCase === 'success' ? SNACKBAR_THEME.GREEN : SNACKBAR_THEME.RED}
            yPosition={'top'}
            xPosition={'center'}
          />
          }

          {/* Confirm Modal */}
          {showConfirmModal && <ConfirmModal
            open={true}
            message={[
              <div>Proceed with selected events only or include recurring ones?</div>,
              <div><Checkbox onChange={(e) => setIsDeleteRecurring(e.target.checked)} />Include recurring events</div>,
              <br />
            ]}
            onActionTaken={(isConfirm) => onConfirmModalAction(isConfirm)}
          />}
        </>
      )}
    </Grid>
  )
}

/**
 * Idle Events
 * @returns 
 */
export function IdleEventsComponent() {

  const [data, setData] = useState([])
  const [showLoader, setShowLoader] = useState(false)
  const [selectedIDList, setSelectedIDList] = useState([])
  const [snackbarMessage, setSnackbarMessage] = useState(null)
  const [snackbarCase, setSnackbarCase] = useState(null)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [isDeleteRecurring, setIsDeleteRecurring] = useState(false)
  const [currentUserAccess, setCurrentUserAccess] = useState(null)
  const [selectedAction, setSelectedAction] = useState(null)
  const [openScheduleIdleDialog, setOpenScheduleIdleDialog] = useState(false)
  const [idleEventsApiData, setIdleEventsApiData] = useState({})
  const location = useLocation()
  const { type } = useCurrentInstanceType()
  const { selectedAccount } = useAuth()
  const { getScopedItem } = useScopedLocalStorage()

  // const [selectedTimezone, setSelectedTimezone] = useState(
  //   Intl.DateTimeFormat().resolvedOptions().timeZone,
  // )

  const dataColumns = type === 'vmss'
    ? vmssInventoryIdleEventColumnList
    : vmInventoryIdleEventColumnList

  /**
   * Instance(s) checkbox click handler
   * @param {*} selectedIDListVal 
   */
  const onDataGridCheckboxSelectHandler = (selectedIDListVal = []) => {
    DebugService.log(`Idle Events Component: selected instance id list`, selectedIDListVal)
    setSelectedIDList(selectedIDListVal)
  }

  /**
   * Delete handler
   */
  const onDeleteClick = () => {
    setShowLoader(true)
    setSnackbarMessage(null)
    setSnackbarCase(null)

    if (selectedIDList.length > 0) {
      let endpoint = APIConstants.ENDPOINT_AZURE_INVENTORY_VM_SCHEDULE_IDLE_DELETE

      // if (type === 'vmss') {
      //   endpoint = APIConstants.ENDPOINT_AZURE_INVENTORY_VMSS_SCHEDULE_IDLE_DELETE
      // }

      const dataRequest = {
        userAccountId: selectedAccount ?? '',
        idleScheduleIds: selectedIDList
      }

      DebugService.log(`Azure Idle Events Component: Delete request is initiated`, dataRequest)

      InventoryApiService.deleteAzureIdleEvents(endpoint, dataRequest)
        .then(res => {
          DebugService.log('Azure Idle Events Component: Data', res)

          if (res['data']) {
            setSnackbarCase('success')
            setSnackbarMessage(`Scheduled instances deleted successfully`)

            setTimeout(() => {
              fetchData()
            }, 1000)
          }

          if (res['error']) {
            setSnackbarCase('failed')
            setSnackbarMessage(res['error']?.['message'])
          }

          setShowLoader(false)
        })
        .catch(err => {
          DebugService.error('Azure Idle Events Component: Error caught while deleting data', err)
          setSnackbarCase('error')
          setSnackbarMessage(`Couldn't delete Scheduled instances due to some error`)
          setShowLoader(false)
        })
    }
  }

  /**
   * Edit handler
   */
  const onEditClick = ({ idleDuration }) => {
    setShowLoader(true)
    setSnackbarMessage(null)
    setSnackbarCase(null)

    if (selectedIDList.length > 0) {
      let endpoint = APIConstants.ENDPOINT_AZURE_INVENTORY_VM_SCHEDULE_IDLE_UPDATE

      // if (type === 'vmss') {
      //   endpoint = APIConstants.ENDPOINT_AZURE_INVENTORY_VMSS_SCHEDULE_IDLE_UPDATE
      // }

      const userDetails = getScopedItem(STORAGE_KEYS.LOCAL_USER_DETAILS)
      const parseToJson = JSON.parse(userDetails)

      const dataRequest = {
        userAccountId: selectedAccount ?? '',
        userId: '',
      }

      // if (type === 'rds') {
      //   dataRequest['dbInstances'] = data.filter(d => selectedIDList.includes(d['id'])).map(d => ({
      //     instanceId: d['InstanceId'],
      //     idleScheduleId: d['id'],
      //     idleDays: idleDuration,
      //     region: d['Region'],
      //   }))
      // } else {
      // VM
      dataRequest['instances'] = data.filter(d => selectedIDList.includes(d['id'])).map(d => ({
        instanceId: d['InstanceId'],
        idleScheduleId: d['id'],
        idleDays: idleDuration,
        region: d['Region'],
        instanceType: d['InstanceType'],
        instanceName: d['InstanceName']
      }))
      // }

      if (parseToJson['userId']) {
        dataRequest['userId'] = parseToJson['userId']
      }

      DebugService.log(`Azure Idle Events Component: Edit request is initiated`, dataRequest)

      InventoryApiService.updateAzureScheduleIdle(endpoint, dataRequest)
        .then(res => {
          DebugService.log('Azure Idle Events Component: Data', res)

          if (res['data']) {
            setOpenScheduleIdleDialog(false)
            setSnackbarCase('success')
            setSnackbarMessage(`Scheduled instances updated successfully`)

            setTimeout(() => {
              fetchData()
            }, 1000)
          }

          if (res['error']) {
            setSnackbarCase('failed')
            setSnackbarMessage(res['error']?.['message'])
          }

          setShowLoader(false)
        })
        .catch(err => {
          DebugService.error('Azure Idle Events Component: Error caught while editing data', err)
          setSnackbarCase('error')
          setSnackbarMessage(`Couldn't edit Scheduled instances due to some error`)
          setShowLoader(false)
        })
    }
  }

  /**
   * Fetch scheduled event list
   */
  const fetchData = async () => {
    setShowLoader(true);

    try {
      setData([])

      let endpoint = APIConstants.ENDPOINT_AZURE_INVENTORY_VM_IDLE_EVENT_LIST

      if (type === 'vmss') {
        endpoint = APIConstants.ENDPOINT_AZURE_INVENTORY_VMSS_IDLE_EVENT_LIST
      }

      InventoryApiService.getAzureIdleEvents(selectedAccount, endpoint)
        .then(res => {
          DebugService.log('Azure Idle Events Component: Data', res)

          setIdleEventsApiData(res?.data ? res : {})

          setData((res['data'] ?? []).map(d => ({
            ...d,
            id: d['IdleScheduleId']
          })))

          setShowLoader(false);
        })
        .catch(err => {
          setShowLoader(false);
          DebugService.error('Azure Idle Events Component: Error caught while fetching data', err)
        })

    } catch (error) {
      setShowLoader(false);
      DebugService.error('Azure Idle Events Component: Fetch data error', error)
    } finally {
    }
  }

  const onConfirmModalAction = (isConfirm) => {
    setShowConfirmModal(false)

    if (isConfirm) {
      if (selectedAction === 'DELETE') {
        onDeleteClick()
      } else if (selectedAction === 'EDIT') {
        setOpenScheduleIdleDialog(true)
      }
    }
  }

  /**
   * Select action handler
   * @param {*} action 
   */
  const onSelectAction = (action) => {
    setSelectedAction(action)
    setShowConfirmModal(true)
  }

  useEffect(() => {
    fetchData()

    const userDetails = getScopedItem(STORAGE_KEYS.LOCAL_USER_DETAILS)
    const parseToJson = JSON.parse(userDetails)
    setCurrentUserAccess(parseToJson['roleName'])
  }, [location.pathname, selectedAccount])

  // useEffect(() => {
  //   setShowLoader(true)
  //   setData(prev => processData(rawData ?? []))
  //   setShowLoader(false)
  // }, [selectedTimezone])

  /**
  * @description Stores the latest API response in storage.
  * @author Ashish Sharma
  */
  useEffect(() => {
    const prev = getScopedItem(STORAGE_KEYS.AI_CHATBOT_REQUEST_DATA) || {};
    setScopedItem(STORAGE_KEYS.AI_CHATBOT_REQUEST_DATA, {
      ...prev,
      idleEvents: idleEventsApiData
    });
  }, [idleEventsApiData]);

  // ── Selection wiring for the new-design Idle Events table ──────────────
  const idleSelectedSet = new Set(selectedIDList);
  const allIdleSelected = data.length > 0 && data.every((r) => idleSelectedSet.has(r.id));
  const toggleIdleRow = (row) =>
    setSelectedIDList((prev) => (prev.includes(row.id) ? prev.filter((id) => id !== row.id) : [...prev, row.id]));
  const toggleIdleAll = (checked) => setSelectedIDList(checked ? data.map((r) => r.id) : []);
  const idleCheckboxColumn = {
    key: 'checkbox',
    label: (
      <input type="checkbox" checked={allIdleSelected} onClick={(e) => e.stopPropagation()} onChange={(e) => toggleIdleAll(e.target.checked)} />
    ),
    render: (row) => (
      <input type="checkbox" checked={idleSelectedSet.has(row.id)} onClick={(e) => e.stopPropagation()} onChange={() => toggleIdleRow(row)} />
    ),
  };

  return (
    <Grid container spacing={1} className='inventory-subpage'>
      {/* Table Loader */}
      {showLoader && <TableLoader />}

      {/* Content */}
      {!showLoader && (
        <>
          <Grid item xs={12} style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <button
              className="btn ghost"
              title="Edit"
              disabled={!selectedIDList.length || currentUserAccess === USER_ROLE.READ_ONLY}
              onClick={() => onSelectAction('EDIT')}
            >
              ✎ Edit
            </button>
            <button
              className="btn ghost"
              title="Delete"
              disabled={!selectedIDList.length || currentUserAccess === USER_ROLE.READ_ONLY}
              onClick={() => onSelectAction('DELETE')}
            >
              🗑 Delete
            </button>
          </Grid>

          <Grid item xs={12}>
            <NewTable
              rows={data}
              columns={[idleCheckboxColumn, ...idleNewColumns]}
              loading={false}
              pageSizes={[10, 25, 50, 100]}
              defaultPageSize={25}
              emptyText="No idle events found."
            />
          </Grid>

          {/* Schedule Idle Modal */}
          {openScheduleIdleDialog && <ScheduleIdle
            style={{ textTransform: 'none' }}
            open={true}
            handleClose={() => setOpenScheduleIdleDialog(false)}
            onSave={onEditClick}
            instanceData={data.filter(d => selectedIDList.includes(d['id'])) ?? []}
          />}

          {snackbarMessage !== null && <SnackbarNotification
            initialOpen={true}
            duration={7000}
            message={snackbarMessage}
            actionButtonName={'Ok'}
            theme={snackbarCase === 'success' ? SNACKBAR_THEME.GREEN : SNACKBAR_THEME.RED}
            yPosition={'top'}
            xPosition={'center'}
          />
          }

          {/* Confirm Modal */}
          {showConfirmModal && <ConfirmModal
            open={true}
            message={[
              <div key={'cmm_1'}>Do you want to {selectedAction.toLocaleLowerCase()} selected instances?</div>,
              <br key={'cmm_2'} />
            ]}
            onActionTaken={(isConfirm) => onConfirmModalAction(isConfirm)}
          />}
        </>
      )}
    </Grid>
  )
}

/**
 * Instance Activity Log Component
 * @param {*} param0 
 * @returns 
 */
export function ActivityLogComponent() {

  const [rawData, setRawData] = useState([])
  const [data, setData] = useState([])
  const [selectedDate, setSelectedDate] = useState(dayjs().startOf('month').utc(true))
  const [selectedToDate, setSelectedToDate] = useState(dayjs())  // add this
  const [isLoading, setIsLoading] = useState(false)
  const [showSnackbarMessage, setShowSnackbarMessage] = useState('HIDE')
  const [asgCostAnchorEl, setAsgCostAnchorEl] = React.useState(null);
  const [popupData, setPopupData] = React.useState(null);
  const [activityEventsApiData, setActivityEventsApiData] = useState({})

  const open = Boolean(asgCostAnchorEl);
  const id = open ? 'simple-popover' : undefined;

  const [selectedTimezone, setSelectedTimezone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone,
  )

  const { getScopedItem } = useScopedLocalStorage()

  const location = useLocation()
  const { type } = useCurrentInstanceType()
  const { selectedAccount } = useAuth()

  const dataColumns = type === 'vmss'
    ? vmssInventoryActivityEventColumnList
    : vmInventoryActivityEventColumnList


  /**
   * @description Asg cost cell click handler
   * @param {*} e 
   * @param {*} data 
   */
  // const asgCostClickedComponent = (e, data) => {
  //   if (data?.['costingDetails']?.['instanceCostingDetails']?.length) {
  //     setAsgCostAnchorEl(e.currentTarget);
  //     setPopupData(data['costingDetails'])
  //   }
  // }

  const processData = (data) => {
    return data.map((d, i) => {
      let startDateTime = d['startTime']
      let stopDateTime = d['endTime']

      if (selectedTimezone?.['value'] && d['startTime']) {
        startDateTime = new Date(d['startTime']).toLocaleString('en-US', {
          timeZone: selectedTimezone['value']
        })
      }

      if (selectedTimezone?.['value'] && d['endTime']) {
        stopDateTime = new Date(d['endTime']).toLocaleString('en-US', {
          timeZone: selectedTimezone['value']
        })
      }

      let result = {
        ...d,
        'id': (i + 1),
      }

      if (startDateTime) {
        result['startTime'] = format(startDateTime, 'dd-MM-yyyy HH:mm:ss')
      }

      if (stopDateTime) {
        result['endTime'] = format(stopDateTime, 'dd-MM-yyyy HH:mm:ss')
      }

      // if (type === 'asg') {
      //   result['asgCostClickedComponent'] = asgCostClickedComponent
      // }

      return result
    })
  }

  useEffect(() => {
    if (selectedDate !== null) {
      setData([])
      setIsLoading(true)
      setShowSnackbarMessage('HIDE')

      const dataRequest = {
        userAccountId: selectedAccount ?? '',
        fromDate: selectedDate.startOf('day'),
        toDate: selectedToDate.endOf('day')
      }

      let endpoint = APIConstants.ENDPOINT_AZURE_INVENTORY_VM_ACTIVTY_EVENTS

      if (type === 'vmss') {
        endpoint = APIConstants.ENDPOINT_AZURE_INVENTORY_VMSS_ACTIVTY_EVENTS
      } else if (['mysql', 'postgresql', 'sql'].includes(type)) {
        dataRequest['serviceType'] = AZURE_DB_TYPE[type] ?? ''
        endpoint = APIConstants.ENDPOINT_AZURE_INVENTORY_DB_ACTIVTY_EVENTS
      }

      InventoryApiService.getAzureActivityInventory(endpoint, dataRequest)
        .then(res => {
          DebugService.log(`Azure Activity Log Component: Data for date ${selectedDate}`, res)

          setActivityEventsApiData(res?.data ? res : {})

          let dateRow = []

          if (res['error']) {
            setShowSnackbarMessage(prev => 'SHOW')
          }

          setRawData(res['data'] ?? [])
          setData(processData(res['data'] ?? []))

          DebugService.log(`Azure Activity Log component: Updated data`, dateRow)
          setIsLoading(false)
        })
        .catch(err => {
          DebugService.log(`Azure Activity Log Component: Error caught while fetching data for date ${selectedDate}`, err)
          setIsLoading(false)
        })
    }
  }, [selectedDate, selectedToDate, location.pathname, selectedAccount])

  useEffect(() => {
    setIsLoading(true)
    setData(prev => processData(rawData ?? []))
    setIsLoading(false)
  }, [selectedTimezone])

  /**
  * @description Stores the latest API response in storage.
  * @author Ashish Sharma
  */
  useEffect(() => {
    const prev = getScopedItem(STORAGE_KEYS.AI_CHATBOT_REQUEST_DATA) || {};
    setScopedItem(STORAGE_KEYS.AI_CHATBOT_REQUEST_DATA, {
      ...prev,
      activityEvents: activityEventsApiData
    });
  }, [activityEventsApiData]);

  return (
    <Grid container className='inventory-subpage'>

      {isLoading && <TableLoader />}

      {!isLoading && (
        <>
          <Grid item xs={12} md={2}></Grid>
          <Grid item xs={12} md={8}>
            <TimezoneSelect value={selectedTimezone} onChange={(v) => {
              setSelectedTimezone(v)
            }} />
          </Grid>
          <Grid item xs={12} md={2}></Grid>
          <Grid item xs={12} md={5}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DemoContainer components={['DatePicker']}>
                <DatePicker
                  label="From Date"
                  value={selectedDate}
                  onChange={setSelectedDate}
                  slots={{
                    textField: (props) => (
                      <TextField {...props} variant="outlined" margin="normal" fullWidth />
                    )
                  }}
                  format="DD/MM/YYYY"
                  onClose={() => { }}
                  maxDate={dayjs()}
                  disableFuture={true}
                />
              </DemoContainer>
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} md={2} />
          <Grid item xs={12} md={5}>
            
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DemoContainer components={['DatePicker']}>
                <DatePicker
                  label="To Date"
                  value={selectedToDate}
                  onChange={setSelectedToDate}
                  slots={{
                    textField: (props) => (
                      <TextField {...props} variant="outlined" margin="normal" fullWidth />
                    )
                  }}
                  format="DD/MM/YYYY"
                  onClose={() => { }}
                  minDate={selectedDate}
                  maxDate={dayjs()}
                  disableFuture={true}
                />
              </DemoContainer>
            </LocalizationProvider>
          </Grid>

          {/* {dateData && */}
          <Grid item xs={12}>
            {/* Popup */}
            <Popover
              id={id}
              open={open}
              anchorEl={asgCostAnchorEl}
              onClose={() => setAsgCostAnchorEl(null)}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}>
              {popupData !== null &&
                <Box className='popup-section' >
                  <TableContainer component={Paper}>
                    <Table sx={{ minWidth: 100 }} size="small" aria-label="a dense table">
                      <TableHead>
                        <TableRow>
                          <TableCell>Type</TableCell>
                          <TableCell>Cost</TableCell>
                          <TableCell>Duration</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {popupData['instanceCostingDetails']
                          && popupData['instanceCostingDetails'].length > 0
                          && popupData['instanceCostingDetails'].map(costDetails => (
                            <TableRow>
                              <TableCell>{costDetails['instanceType']}</TableCell>
                              <TableCell>{costDetails['cost']}</TableCell>
                              <TableCell>{costDetails['duration']}</TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              }
            </Popover>

            <NewTable
              rows={data}
              columns={activityEventNewColumns}
              loading={false}
              pageSizes={[10, 25, 50, 100]}
              defaultPageSize={25}
              emptyText="No activity found for the selected date."
            />
          </Grid>

          {showSnackbarMessage === 'SHOW' && <SnackbarNotification
            initialOpen={true}
            duration={7000}
            message={'No activity found for the selected date'}
            actionButtonName={'Ok'}
            theme={SNACKBAR_THEME.RED}
            yPosition={'top'}
            xPosition={'center'}
          />
          }
        </>
      )}
    </Grid>
  )
};

/**
 * Instance Modification Log Component
 * @param {*} param0
 * @returns
 */
// export function ModificationLogComponent() {

//   const [fromDate, setFromDate] = useState(dayjs(new Date(new Date().setDate((new Date()).getDate() - 2))))
//   const [toDate, setToDate] = useState(dayjs(new Date(yesterdayDate())))
//   const [changesInstances, setChangesInstances] = useState([])
//   const [addedInstances, setAddedInstances] = useState([])
//   const [removedInstances, setRemovedInstances] = useState([])
//   const [showLoading, setShowLoading] = useState(false)
//   const [showSnackbarMessage, setShowSnackbarMessage] = useState('HIDE')
//   const [selectedTab, setSelectedTab] = useState(INVENTORY_EVENTS.MODIFICATION_CHANGES)

//   const location = useLocation()
//   const { type } = useCurrentInstanceType()
//   const { selectedAccount } = useAuth()

//   const changesColumns = type === 'rds'
//     ? rdsModificationChangesColumns
//     : type === 'asg'
//       ? asgModificationLogColumnsForChanges
//       : ec2ModificationChangesColumns

//   const addedColumns = type === 'rds'
//     ? rdsColumnsForModificationEvents
//     : type === 'asg'
//       ? asgModificationLogColumns
//       : ec2ModificationLogColumnsForDataGrid

//   const getLogs = () => {
//     if (fromDate && toDate) {
//       setShowLoading(true)
//       setShowSnackbarMessage('HIDE')

//       const dateRequestBody = {
//         fromDate: encodeURIComponent(fromDate.toISOString()),
//         toDate: encodeURIComponent(toDate.toISOString())
//       }

//       let endpoint = APIConstants.ENDPOINT_INVENTORY_EC2_CHANGES

//       if (type === 'rds') {
//         endpoint = APIConstants.ENDPOINT_INVENTORY_RDS_CHANGES
//       }

//       if (type === 'asg') {
//         endpoint = APIConstants.ENDPOINT_INVENTORY_ASG_CHANGES
//       }

//       InventoryApiService.getModifiedEvents(selectedAccount, endpoint, dateRequestBody)
//         .then(res => {
//           DebugService.log(`Modification Log Component: API Response`, res)

//           const data = res['data'] ?? null
//           let addedData = []
//           let removedData = []

//           if (!res['data']) {
//             setShowSnackbarMessage('SHOW')
//           }

//           if (data && data['addedInstances']) {
//             if (type === 'rds') {
//               addedData = data['addedInstances'].map((instance, i) => {
//                 return {
//                   ...instance,
//                   id: i + 1,
//                 }
//               })
//             } else if (type === 'asg') {
//               addedData = data['addedInstances'].map((instance, i) => {
//                 return {
//                   ...instance,
//                   id: instance['InstanceId'],
//                   name: instance['AutoScalingGroupName']
//                 }
//               })
//             } else {
//               addedData = data['addedInstances'].map(instance => ({
//                 ...instance,
//                 "name": instance.name,
//                 'id': instance.Instance_ID,
//                 'instanceType': instance.Instance_type,
//                 'availabilityZone': instance.Availability_zone,
//                 'publicIPv4DNS': instance.Public_IPv4_DNS,
//                 'publicIPv4Address': instance.Public_IPv4_address,
//                 'iPv6IPs': instance?.IPv6_IPs,
//                 'monitoring': instance.Monitoring,
//                 'securityGroupName': instance.Security_group_name,
//                 'keyName': instance.Key_name,
//                 'launchTime': format(instance.Launch_Time, 'dd-MM-yyyy HH:mm:ss'),
//                 'platfromDetails': instance.Platform_details,
//               }))
//             }

//             setAddedInstances(addedData)
//           }

//           if (data && data['removedInstances']) {
//             if (type === 'rds') {
//               removedData = data['removedInstances'].map((instance, i) => {
//                 return {
//                   ...instance,
//                   id: i + 1,
//                 }
//               })

//             } else if (type === 'asg') {
//               removedData = data['removedInstances'].map((instance, i) => {
//                 return {
//                   ...instance,
//                   id: instance['InstanceId'],
//                   name: instance['AutoScalingGroupName']
//                 }
//               })
//             } else {
//               removedData = data['removedInstances'].map(instance => ({
//                 ...instance,
//                 "name": instance.name,
//                 'id': instance.Instance_ID,
//                 'instanceType': instance.Instance_type,
//                 'availabilityZone': instance.Availability_zone,
//                 'publicIPv4DNS': instance.Public_IPv4_DNS,
//                 'publicIPv4Address': instance.Public_IPv4_address,
//                 'iPv6IPs': instance?.IPv6_IPs,
//                 'monitoring': instance.Monitoring,
//                 'securityGroupName': instance.Security_group_name,
//                 'keyName': instance.Key_name,
//                 'launchTime': format(instance.Launch_Time, 'dd-MM-yyyy HH:mm:ss'),
//                 'platfromDetails': instance.Platform_details,
//               }))
//             }

//             setRemovedInstances(removedData)
//           }

//           let changesInstances = [];

//           addedData?.forEach(newInstance => {
//             const matchingOldInstance = removedData?.find(
//               oldInstance =>
//                 oldInstance['id'] === newInstance['id'] ||
//                 oldInstance['name'] === newInstance['name']
//             );

//             if (matchingOldInstance) {
//               // Check for changes in each property
//               let updatedInstance = JSON.parse(JSON.stringify(newInstance))

//               for (const key in newInstance) {
//                 if (newInstance.hasOwnProperty(key) && matchingOldInstance.hasOwnProperty(key)) {
//                   if (newInstance[key] !== matchingOldInstance[key]) {

//                     updatedInstance[key] = {
//                       classname: 'highlight-change',
//                       updatedValue: newInstance[key],
//                       oldValue: matchingOldInstance[key],
//                     }

//                   }
//                 }
//               }
//               changesInstances = [
//                 ...changesInstances,
//                 updatedInstance
//               ]

//             }
//           });

//           setChangesInstances(changesInstances)
//           DebugService.log(`Modification Log Component: Changes data`, changesInstances)
//           setShowLoading(false)
//         })
//         .catch(err => {
//           DebugService.log(`Modification Log Component: Couldn't fetch data`, err)
//           setShowLoading(false)
//         })

//     }
//   }

//   useEffect(() => {
//     setAddedInstances([])
//     setRemovedInstances([])
//     setChangesInstances([])

//     getLogs()
//   }, [location.pathname, selectedAccount])

//   return (
//     <Grid container spacing={1} className='inventory-subpage'>
//       {showLoading && <TableLoader />}

//       {!showLoading && (
//         <>
//           <Grid item xs={12} md={4}>
//             <LocalizationProvider dateAdapter={AdapterDayjs}>
//               <DemoContainer components={['DatePicker']}>
//                 <DatePicker
//                   label="From Date"
//                   value={fromDate}
//                   onChange={(newDate) => setFromDate(newDate.startOf('day'))}
//                   slots={{
//                     textField: (props) => (
//                       <TextField {...props} variant="outlined" margin="normal" fullWidth size='small' />
//                     )
//                   }}
//                   format="DD/MM/YYYY"
//                   onClose={() => { }}
//                   maxDate={dayjs(new Date(yesterdayDate()))}
//                   disableFuture={true}
//                 />
//               </DemoContainer>
//             </LocalizationProvider>
//           </Grid>
//           <Grid item xs={12} md={4}>
//             <LocalizationProvider dateAdapter={AdapterDayjs}>
//               <DemoContainer components={['DatePicker']}>
//                 <DatePicker
//                   label="To Date"
//                   value={toDate}
//                   onChange={(newDate) => setToDate(newDate.startOf('day'))}
//                   slots={{
//                     textField: (props) => (
//                       <TextField {...props} variant="outlined" margin="normal" fullWidth size='small' />
//                     )
//                   }}
//                   format="DD/MM/YYYY"
//                   onClose={() => { }}
//                   maxDate={dayjs(new Date(yesterdayDate()))}
//                   disableFuture={true}
//                 />
//               </DemoContainer>
//             </LocalizationProvider>
//           </Grid>
//           <Grid item xs={12} md={4}>
//             <Button
//               className='refresh'
//               variant="contained"
//               color="primary"
//               onClick={() => getLogs()}
//               disabled={!toDate || !fromDate}
//             >
//               Fetch Logs
//             </Button>
//           </Grid>

//           <Grid item xs={12} >
//             <TabContext value={selectedTab}>
//               <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
//                 <TabList onChange={(e, currentTab) => setSelectedTab(currentTab)} aria-label="Inventory Modification Tab List">
//                   <Tab label={`${INVENTORY_EVENTS.MODIFICATION_CHANGES} ${changesInstances?.length === 0 ? `(0)` : ''}`} value={INVENTORY_EVENTS.MODIFICATION_CHANGES} className='tab' />
//                   <Tab label={`${INVENTORY_EVENTS.MODIFICATION_ADDED} ${addedInstances?.length === 0 ? `(0)` : ''}`} value={INVENTORY_EVENTS.MODIFICATION_ADDED} className='tab' />
//                   <Tab label={`${INVENTORY_EVENTS.MODIFICATION_REMOVED} ${removedInstances?.length === 0 ? `(0)` : ''}`} value={INVENTORY_EVENTS.MODIFICATION_REMOVED} className='tab' />
//                 </TabList>
//               </Box>
//               <TabPanel value={INVENTORY_EVENTS.MODIFICATION_CHANGES} className='tab-panel'>
//                 <DataGridTable
//                   evenRowBgColor={''}
//                   headerBgColor={'#506C84'}
//                   actionIconColor={'#506C84'}
//                   evenRowColor={'#506C84'}
//                   columns={changesColumns}
//                   rows={changesInstances}
//                   showTooltip={false}
//                   showPagination={true}
//                 />
//               </TabPanel>
//               <TabPanel value={INVENTORY_EVENTS.MODIFICATION_ADDED} className='tab-panel'>
//                 <DataGridTable
//                   evenRowBgColor={''}
//                   headerBgColor={'#506C84'}
//                   actionIconColor={'#506C84'}
//                   evenRowColor={'#506C84'}
//                   columns={addedColumns}
//                   rows={addedInstances}
//                   showTooltip={false}
//                   showPagination={true}
//                 />
//               </TabPanel>
//               <TabPanel value={INVENTORY_EVENTS.MODIFICATION_REMOVED} className='tab-panel'>
//                 <DataGridTable
//                   evenRowBgColor={''}
//                   headerBgColor={'#506C84'}
//                   actionIconColor={'#506C84'}
//                   evenRowColor={'#506C84'}
//                   columns={addedColumns}
//                   rows={removedInstances}
//                   showTooltip={false}
//                   showPagination={true}
//                 />
//               </TabPanel>
//             </TabContext>
//           </Grid>

//           {showSnackbarMessage === 'SHOW' && <SnackbarNotification
//             initialOpen={true}
//             duration={7000}
//             message={'No changes found for the selected date'}
//             actionButtonName={'Ok'}
//             theme={SNACKBAR_THEME.RED}
//             yPosition={'top'}
//             xPosition={'center'}
//           />
//           }
//         </>
//       )}
//     </Grid>
//   )
// }
